import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  apiService,
  ApiResponse,
  DirectSearchBackend,
  DirectSearchFacets,
  DirectSearchFilters,
  DirectSearchHistoryEntry as ServerHistoryEntry,
  SearchV2Meta,
  searchMethodLabel,
} from '../services/apiService';
import SearchThinking from './SearchThinking';
import { FloatingToast } from './InlineAlert';
import { keywordsCache } from '../utils/keywordsCache';
import { ChevronDownIcon } from './icons/ChevronDownIcon';
import { ChevronUpIcon } from './icons/ChevronUpIcon';
import { trackClick } from '../utils/analytics';
import { useSlowLoadWarning } from '../utils/useSlowLoadWarning';
import { ModalType } from '../types';
import { InviteIcon, FeedbackIcon, BugIcon } from './icons';
import { useUpgradeGate } from './useUpgradeGate';
import { TechAccordionCard, CardContactTarget } from './techcard/TechAccordionCard';
import { useTechCardDesign } from './techcard/track';
import { resultRelevance } from './techcard/relevance';
import { FilterUpgradeCard, TrialUpgradeCard, UpgradeBanner, UpgradeSearchButton, useFilterCardOn, useSearchButtonOn } from './upgrade/UpgradePrompts';
import { takeCosmosSeed } from '../utils/cosmosSeed';
import { ExplorerPicks, PickCard, PicksSkeleton, PicksViewMode } from './cosmos/ExplorerPicks';
import type { PicksTrialInfo } from './cosmos/PicksUpgradeBlock';
import { searchLockedFor, type ExplorerLandingProps, type PicksQuery, type PicksResponse } from '../types/onboarding';

interface DirectSearchPageProps {
  onReadMore?: (id: string, relevance?: number) => void;
  /** Opens the global Invite / Feedback / Bug modals (matches Cosmos sidebar). */
  openModal?: (type: ModalType) => void;
  /** Accordion cards: "Contact University" (App runs the gate + ContactTTO modal).
   *  `surface` is 'cosmos_picks' for a card on the Explorer landing, else unset. */
  onContact?: (card: CardContactTarget, surface?: 'search_results' | 'cosmos_picks') => void;
  /**
   * Onboarding v2's Explorer landing (App, from the public onboarding config):
   * an Explorer with the lock on sees their 3 picks and the upgrade block in
   * place of Popular. `pending` while App is still learning the flow (the
   * config and onboarding status are in flight), so an Explorer never sees
   * Popular flash first. Absent: the page behaves as before onboarding v2.
   */
  explorerLanding?: ExplorerLandingProps & { pending?: boolean };
  /** The landing block's "Start free trial": App's trial sheet, in place.
   *  `origin` lands on the trial row ('onboarding_picks_block'). */
  onStartTrial?: (origin: string) => void;
  /** The billing summary's trial payload: whether the block offers a trial. */
  trial?: PicksTrialInfo | null;
}

/** paywall_shown's `filter` for a locked filter tick. */
const FILTER_NAME: Partial<Record<keyof DirectSearchFilters, string>> = {
  countries: 'country', university_codes: 'university', university_names: 'university',
  major_categories: 'industry', trl_levels: 'trl',
};

/** The technology id a result row carries. */
const resultTechId = (r: any): string => {
  const m = r?.metadata || {};
  return String(m.utech_technology_id || m.id || '');
};

// ---------------------------------------------------------------------------
// Industry taxonomy (frontend-only for now — backend will mirror later).
//
// `DEFAULT_INDUSTRY_CATEGORIES` is the canonical filter list shown in the UI:
// every other category either reassigns to one of these, or falls into the
// "Others" bucket. Counts shown next to each filter row are aggregated across
// all underlying real categories that map to it.
//
// When the user toggles a default in the filter UI, we expand it into the
// underlying categories before sending the request, so the backend (which
// still sees the raw taxonomy) returns the right rows.
// ---------------------------------------------------------------------------
const DEFAULT_INDUSTRY_CATEGORIES: string[] = [
  'Healthcare & Life Sciences',
  'Advanced Materials & Manufacturing',
  'Electronics, IoT & Robotics',
  'ICT, AI & Computing',
  'Energy & Sustainability',
  'Agriculture & Food',
  'Transportation & Mobility',
  'Construction & Smart Cities',
  'Safety, Defense & First Responders',
  'Consumer Goods & Services',
  'Education, HR & Training',
  'Science, Exploration & Space',
  'Infrastructure, Logistics & Utilities',
  'Creative Works & Copyright',
];

const INDUSTRY_REASSIGNMENT: Record<string, string> = {
  'Chemistry and Chemical Analysis': 'Healthcare & Life Sciences',
  'Engineering : Electric machines': 'Electronics, IoT & Robotics',
  'Optics & Imaging': 'Electronics, IoT & Robotics',
  'Environment & Sustainability': 'Energy & Sustainability',
  'Environment': 'Energy & Sustainability',
  'Imaging & Optics': 'Electronics, IoT & Robotics',
  'Biotechnology': 'Healthcare & Life Sciences',
  'MedTech': 'Healthcare & Life Sciences',
  'Energy & Environment': 'Energy & Sustainability',
  'Biotech Research Tools & Reagents': 'Healthcare & Life Sciences',
  'Digital & Information Technology': 'ICT, AI & Computing',
  'Electronics & Computing': 'ICT, AI & Computing',
  'Advanced Manufacturing & Automation': 'Advanced Materials & Manufacturing',
  'Environment and Sustainability': 'Energy & Sustainability',
  'Environmental': 'Energy & Sustainability',
  'Optics & Photonics': 'Electronics, IoT & Robotics',
  'Research Tools': 'ICT, AI & Computing',
  'Information Technology': 'ICT, AI & Computing',
  'Materials & Chemicals': 'Advanced Materials & Manufacturing',
  'Environmental Monitoring': 'Energy & Sustainability',
  'Design and Multimedia': 'ICT, AI & Computing',
  'Agriculture & Environment': 'Agriculture & Food',
  'Sensors & Instrumentation': 'Electronics, IoT & Robotics',
  // FinTech is too small to deserve its own bucket (only 1 record at last
  // count). Fold it into ICT, AI & Computing.
  'Financial Technology (FinTech)': 'ICT, AI & Computing',
};

const DEFAULT_INDUSTRY_SET = new Set(DEFAULT_INDUSTRY_CATEGORIES);
const REASSIGNMENT_SOURCES = new Set(Object.keys(INDUSTRY_REASSIGNMENT));

// ---------------------------------------------------------------------------
// University short-form aliases — extra search keys for the University
// filter so users typing the common short form (IIT Bombay, MIT, RWTH …)
// match the full canonical name.
// ---------------------------------------------------------------------------
const UNIVERSITY_ALIASES: Record<string, string[]> = {
  'Massachusetts Institute of Technology': ['MIT'],
  'Indian Institute of Technology Bombay': ['IIT Bombay', 'IITB'],
  'Indian Institute of Technology Madras': ['IIT Madras', 'IITM'],
  'Indian Institute of Technology Delhi': ['IIT Delhi', 'IITD'],
  'Indian Institute of Science': ['IISc', 'IISC', 'Indian Institute of Science Bangalore'],
  'Indian Institute of Technology Kanpur': ['IIT Kanpur', 'IITK'],
  'Indian Institute of Technology Kharagpur': ['IIT Kharagpur', 'IIT KGP', 'IITKGP'],
  'Indian Institute of Technology Roorkee': ['IIT Roorkee', 'IITR'],
  'Indian Institute of Technology Guwahati': ['IIT Guwahati', 'IITG'],
  'Stanford University': ['Stanford'],
  'Oxford University': ['Oxford'],
  'University of Toronto': ['Toronto', 'U of T'],
  'University of California, Los Angeles': ['UCLA'],
  'University of California, San Diego': ['UCSD', 'UC San Diego'],
  'University of California, Berkeley': ['UC Berkeley', 'Berkeley'],
  'University of Pennsylvania': ['Penn', 'UPenn'],
  'University of Wisconsin-Madison': ['Wisconsin', 'UW Madison'],
  'University of Minnesota': ['Minnesota', 'UMN'],
  'University of Maryland': ['Maryland', 'UMD'],
  'University of Arizona': ['Arizona', 'UA'],
  'University of Pittsburgh': ['Pitt', 'Pittsburgh'],
  'University of Melbourne': ['Melbourne'],
  'The University of Melbourne': ['Melbourne'],
  'RWTH Aachen University': ['RWTH', 'RWTH Aachen', 'Aachen'],
  'Karlsruhe Institute of Technology (KIT)': ['KIT', 'Karlsruhe'],
  'Council of Scientific & Industrial Research (CSIR)': ['CSIR'],
  'SRM Institute of Science & Technology (SRMIST)': ['SRM', 'SRMIST'],
  'Monash University': ['Monash'],
  'Johns Hopkins University': ['Johns Hopkins', 'JHU'],
  'Cornell University': ['Cornell'],
  'Columbia University': ['Columbia'],
  'Purdue University': ['Purdue'],
  'Arizona State University': ['ASU', 'Arizona State'],
  'Newcastle University': ['Newcastle'],
  'Durham University': ['Durham'],
  'State University of New York': ['SUNY'],
  'Max Planck Innovation': ['Max Planck'],
  'Vellore Institute of Technology (VIT)': ['VIT'],
  'University of Florida (UFI)': ['UFI'],
  'National Research Development Corporation (NRDC)': ['NRDC'],
};

/**
 * Real categories that the backend should be filtered on when the user picks
 * a UI bucket. For a default bucket, it's the default itself + every category
 * that reassigns to it.
 */
const expandDefaultBucket = (defaultCat: string): string[] => {
  const reassignedSources = Object.entries(INDUSTRY_REASSIGNMENT)
    .filter(([, target]) => target === defaultCat)
    .map(([source]) => source);
  return [defaultCat, ...reassignedSources];
};

/**
 * Real categories that fall into the "Others" UI bucket: everything the
 * backend reports that's neither a known default nor mapped to one.
 */
const computeOthersBucket = (allKnown: string[]): string[] =>
  allKnown.filter(c => !DEFAULT_INDUSTRY_SET.has(c) && !REASSIGNMENT_SOURCES.has(c));

/**
 * Local history shape used by the right pane. We keep one consistent shape
 * regardless of whether the entry came from sessionStorage (anonymous fallback)
 * or from the DB-backed endpoint (logged-in users).
 */
interface DirectSearchHistoryEntry {
  id: string;
  query: string;
  filters: DirectSearchFilters;
  /** The backend literal a replay runs on (always one we still serve). */
  backend: DirectSearchBackend;
  /**
   * What the search REALLY ran on, verbatim as stored server-side —
   * "v2:rag_pinecone:typesense", "v2:rag_pinecone:deep", "hybrid" … Display
   * only; `backend` above is the replayable literal.
   */
  method?: string;
  total: number;
  ts: number; // ms since epoch — derived from created_at when coming from server
}

const HISTORY_KEY = 'direct_search_history_v2';
const HISTORY_MAX = 30;
const HITS_PER_PAGE = 10;
// A ceiling, not the page count. The server says how many pages its ranked
// set actually has (nb_pages); this only stops a pathological number of pager
// buttons. It used to be 10, which hid everything past the first 100 results
// even though the server had up to 250 cached and ready to slice.
const MAX_PAGES = 25;
const OTHERS_VALUE = '__others__';

/**
 * The backend a search runs on. Algolia is deprecated — hybrid (Typesense +
 * Pinecone, fused and reranked) is what the product uses.
 *
 * This matters more than it looks. Replaying a search-history entry is the
 * ONLY thing that ever calls setBackend, and every stored row predates hybrid
 * so they all say 'algolia'. Honouring that pinned the whole session to the
 * deprecated backend after a single history click, with no UI to switch back
 * — a page reload was the only way out. A stored value is therefore only
 * honoured when it names a backend we still run.
 */
const DEFAULT_BACKEND: DirectSearchBackend = 'hybrid';

/** Old behaviour (global counts only) is one flag away, per the spec note. */
const DYNAMIC_FACET_COUNTS = true;
const normaliseBackend = (b: unknown): DirectSearchBackend =>
  b === 'hybrid' || b === 'native' ? b : DEFAULT_BACKEND;

const loadHistoryFromSession = (): DirectSearchHistoryEntry[] => {
  try {
    const raw = sessionStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((e: DirectSearchHistoryEntry) => ({
      ...e, backend: normaliseBackend(e.backend),
    }));
  } catch {
    return [];
  }
};

const saveHistoryToSession = (entries: DirectSearchHistoryEntry[]) => {
  try {
    sessionStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(0, HISTORY_MAX)));
  } catch {
    /* sessionStorage might be full */
  }
};

const serverEntryToLocal = (e: ServerHistoryEntry): DirectSearchHistoryEntry => ({
  id: e.id,
  query: e.query || '',
  filters: e.filters || {},
  backend: normaliseBackend(e.backend),
  method: typeof e.backend === 'string' && e.backend ? e.backend : undefined,
  total: e.total || 0,
  ts: e.created_at ? new Date(e.created_at).getTime() : Date.now(),
});

// Content equality for filter sets — object identity is useless here because
// every setFilters produces a fresh object. Order-insensitive on the arrays.
const filtersKey = (f: DirectSearchFilters): string => JSON.stringify({
  c: [...(f.countries || [])].sort(),
  u: [...(f.university_codes || [])].sort(),
  n: [...(f.university_names || [])].sort(),
  m: [...(f.major_categories || [])].sort(),
  t: [...(f.trl_levels || [])].sort((a, b) => a - b),
  lo: f.trl_min ?? null,
  hi: f.trl_max ?? null,
});
const filtersEqual = (a: DirectSearchFilters, b: DirectSearchFilters): boolean =>
  filtersKey(a) === filtersKey(b);

// "No thanks" on the deep-search offer is remembered per (query, filters) for
// the tab's lifetime, so paging through the same result set — or coming back
// to it from history — does not nag again. A different query is a new offer.
const DEEP_DISMISS_KEY = 'direct_search_deep_dismissed_v1';
const deepOfferKey = (q: string, f: DirectSearchFilters): string =>
  `${q.trim().toLowerCase()}|${filtersKey(f)}`;
const loadDeepDismissed = (): Set<string> => {
  try {
    const raw = sessionStorage.getItem(DEEP_DISMISS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x: unknown) => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
};
const saveDeepDismissed = (keys: Set<string>) => {
  try {
    sessionStorage.setItem(DEEP_DISMISS_KEY, JSON.stringify(Array.from(keys).slice(-50)));
  } catch {
    /* sessionStorage might be full */
  }
};

/** Everything "Back to quick results" needs to put the quick page back without a request. */
interface QuickSnapshot {
  results: any[];
  total: number;
  totalUncapped: number;
  hasMoreBeyondCap: boolean;
  liveFacetCounts: Record<string, Record<string, number>> | null;
  page: number;
  pageSize: number;
  meta: SearchV2Meta | null;
  aiApplied: string[];
  lockedDetected: string[];
  emptyNotice: string;
  correctedQuery: string;
}

const filtersAreEmpty = (f: DirectSearchFilters): boolean =>
  (!f.countries || !f.countries.length) &&
  (!f.university_codes || !f.university_codes.length) &&
  (!f.major_categories || !f.major_categories.length) &&
  (!f.trl_levels || !f.trl_levels.length) &&
  (f.trl_min === null || f.trl_min === undefined) &&
  (f.trl_max === null || f.trl_max === undefined);

/** The TRL levels an AI filter names: trl_levels as sent (1-9), or the
 *  trl_min..trl_max range expanded — the panel ticks levels, not ranges. */
const aiTrlLevels = (ai: DirectSearchFilters | undefined | null): number[] => {
  if (Array.isArray(ai?.trl_levels) && ai!.trl_levels!.length) {
    return ai!.trl_levels!.filter(n => n >= 1 && n <= 9);
  }
  const levels: number[] = [];
  if (typeof ai?.trl_min === 'number' || typeof ai?.trl_max === 'number') {
    const lo = Math.max(1, Math.min(9, ai?.trl_min ?? ai?.trl_max ?? 1));
    const hi = Math.max(1, Math.min(9, ai?.trl_max ?? ai?.trl_min ?? 9));
    for (let n = Math.min(lo, hi); n <= Math.max(lo, hi); n++) levels.push(n);
  }
  return levels;
};

/** Chip text for TRL levels sorted ascending. A vetoed middle level leaves a
 *  hole — "TRL 5–7" would claim the vetoed 6 is applied — so it reads as a
 *  range only when it actually is one. */
const trlChipLabel = (sorted: number[]): string => {
  const lo = sorted[0];
  const hi = sorted[sorted.length - 1];
  const contiguous = sorted.length === hi - lo + 1;
  return sorted.length === 1 ? `TRL ${lo}`
    : contiguous ? `TRL ${lo}–${hi}`
    : `TRL ${sorted.join(', ')}`;
};

/** Chip text for filters found in the query but withheld from an account that
 *  cannot filter: the server's meta.filters.locked_labels when it sent them,
 *  else built from the filters (university, country, TRL). */
const lockedFilterLabels = (labels: unknown, ai: DirectSearchFilters | undefined | null): string[] => {
  if (Array.isArray(labels)) {
    const sent = labels.map(l => String(l ?? '').trim()).filter(Boolean);
    if (sent.length) return sent;
  }
  const out: string[] = [];
  const add = (v: unknown) => {
    const s = String(v ?? '').trim();
    if (s && !out.includes(s)) out.push(s);
  };
  (ai?.university_names || []).forEach(add);
  (ai?.countries || []).forEach(add);
  (ai?.major_categories || []).forEach(add);
  const levels = Array.from(new Set(aiTrlLevels(ai))).sort((a, b) => a - b);
  if (levels.length) add(trlChipLabel(levels));
  return out;
};

const summarizeFilters = (f: DirectSearchFilters): string => {
  const parts: string[] = [];
  if (f.countries?.length) parts.push(`${f.countries.length} country`);
  if (f.university_codes?.length) parts.push(`${f.university_codes.length} uni`);
  if (f.major_categories?.length) parts.push(`${f.major_categories.length} cat`);
  if (f.trl_levels?.length) parts.push(`TRL ${f.trl_levels.sort().join(',')}`);
  return parts.join(' · ');
};

const DirectSearchPageInner: React.FC<DirectSearchPageProps> = ({
  onReadMore, openModal, onContact, explorerLanding, onStartTrial, trial,
}) => {
  const [facets, setFacets] = useState<DirectSearchFacets | null>(null);
  // Free-tier locks. Filters and running a search are paid; typing and seeing
  // suggestions stay open so the product still feels alive before paying.
  // With search locked (Admin → Onboarding → "Lock search for Explorers",
  // served as the public config's search_locked; on an older server, the
  // free-tier lock's can_search) the box itself is read-only and any attempt
  // opens the plans sheet.
  const upgrade = useUpgradeGate();
  // ---------- Explorer landing (onboarding v2) ----------
  // Only on a REAL answer: a failed entitlements lookup renders the CLOSED
  // fallback, and a paying user whose lookup failed must never be given the
  // Explorer layout or a locked search box.
  const landing = explorerLanding;
  const lockKnown = upgrade.ready && !upgrade.loadFailed;
  const lockedAccount = lockKnown && !upgrade.ent.paid && !!upgrade.ent.lock_enabled;
  const explorerView = !!landing?.enabled && lockedAccount;
  // The server's switch when App has it, else the entitlements (as before).
  // Both are read once per page load, so a change in the admin reaches an
  // Explorer on their next load (the public config is cached a minute).
  const searchLocked = lockKnown && searchLockedFor(landing?.searchLocked, upgrade.ent);
  // Where a lock was hit, for paywall_shown / search_locked_click.
  const lockSurface = explorerView ? 'cosmos_picks' : 'cosmos';
  /**
   * What the page shows before any search:
   *   wait    not known yet — skeleton, never Popular (an Explorer would see
   *           10 cards flash, then 3); also while the onboarding modal is up
   *   picks   an Explorer on onboarding v2: their 3 cards + the upgrade block
   *   popular everyone else, exactly as before
   */
  const landingMode: 'wait' | 'picks' | 'popular' =
    !landing ? 'popular'
      : !upgrade.ready && (landing.enabled || landing.pending) ? 'wait'
      : lockedAccount && landing.pending ? 'wait'
      : explorerView && landing.onboardingOpen ? 'wait'
      : explorerView ? 'picks' : 'popular';
  const landingModeRef = useRef(landingMode);
  landingModeRef.current = landingMode;
  const landingRef = useRef(landing);
  landingRef.current = landing;
  // The response behind the picks on screen (mode, industry, keyword for the
  // heading), and the rows array itself: the list on screen IS the picks
  // exactly while `results` is that array.
  const [picksMeta, setPicksMeta] = useState<PicksResponse | null>(null);
  const [picksLoading, setPicksLoading] = useState(false);
  const picksRowsRef = useRef<any[] | null>(null);
  // The onboarding answer (its `at`) whose first landing was already reported.
  const firstLandingRef = useRef<number>(0);
  // A tech page's Industry insights hand-off, for an Explorer: picks for that
  // industry rather than a locked filter (which would open the sheet on entry).
  const seedIndustryRef = useRef<string>('');
  // Armed once per typed query, so one event per search attempt rather than
  // one per keystroke.
  const searchIntentRef = useRef(false);
  // The paywall modal is mounted ONCE, in App — useUpgradeGate's shared store
  // depends on that. This page used to mount a second copy that painted
  // underneath App's and wired its Upgrade button to the invite form.
  // Mirror for async callbacks — reflection runs after a multi-second search
  // round-trip; a search launched before /facets resolved must still see the
  // facets that arrived mid-flight (same staleness hazard as filtersRef).
  const facetsRef = useRef<DirectSearchFacets | null>(null);
  facetsRef.current = facets;
  const [facetsLoading, setFacetsLoading] = useState(true);
  // Hybrid is the primary search path: Typesense + Pinecone fused with RRF,
  // cross-encoder reranked, with an LLM layer that lifts TRL / country /
  // university out of the query text and applies them as filters. Algolia and
  // native remain available (history entries restore whichever was used), and
  // the backend transparently falls back to Algolia if the hybrid stack is
  // unavailable — so this default cannot break search.
  const [backend, setBackend] = useState<DirectSearchBackend>(DEFAULT_BACKEND);
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<DirectSearchFilters>({});
  const [results, setResults] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [hasMoreBeyondCap, setHasMoreBeyondCap] = useState<boolean>(false);
  // How many technologies actually match, as opposed to how many we let the
  // user page through. Hybrid fetches ~50 from each leg and de-duplicates, so
  // `total` lands near 100 regardless of whether 120 or 12,000 matched.
  const [totalUncapped, setTotalUncapped] = useState<number>(0);
  /**
   * Filter counts for the CURRENT result set, returned by the search response
   * as `facet_counts`. The backend has always sent these; the panel only ever
   * read the global /facets payload, so every count stayed at its whole-corpus
   * value no matter what was filtered or searched (spec TC-01..TC-08).
   *
   * Null until a search runs — that is TC-01, where the global numbers ARE the
   * right answer. Set DYNAMIC_FACET_COUNTS to false to go back to the old
   * always-global behaviour.
   */
  const [liveFacetCounts, setLiveFacetCounts] = useState<Record<string, Record<string, number>> | null>(null);
  const [page, setPage] = useState<number>(0);
  // Hits per page of the CURRENT result set. The keyword routes page at
  // HITS_PER_PAGE; the engine (RAG) route ships its whole top_n (25) in one
  // page, so the pager must not promise pages 2 and 3 that hold nothing.
  const [pageSize, setPageSize] = useState<number>(HITS_PER_PAGE);
  /** nb_pages as the server computed it, over the whole ranked set. */
  const [serverPages, setServerPages] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  // True while the in-flight search keeps the current list on screen (paging
  // served from the server cache, a deep search) instead of swapping it for
  // the full "Searching…" loader. Cleared with `loading`.
  const [loadingInline, setLoadingInline] = useState(false);
  const [pendingPage, setPendingPage] = useState(0);
  // `meta` of the response on screen — the v2 route/engine/cache diagnostics
  // and the deep-search offer flag. Null for the legacy endpoint and Popular.
  const [responseMeta, setResponseMeta] = useState<SearchV2Meta | null>(null);
  // The words the quick route actually searched after a typo fix ("batery" →
  // "battery"), kept only when they differ from what was typed. Empty: no
  // correction, so no "Showing results for" line.
  const [correctedQuery, setCorrectedQuery] = useState<string>('');
  // Deep search = the same query re-run with force_route 'engine' (the RAG
  // engine reads the results with an LLM). 'running' keeps the quick results
  // dimmed but visible; 'active' means the list IS the deep result set and
  // quickSnapshotRef holds the quick page to go back to without a request.
  const [deepState, setDeepState] = useState<'idle' | 'running' | 'active'>('idle');
  const quickSnapshotRef = useRef<QuickSnapshot | null>(null);
  const [deepDismissed, setDeepDismissed] = useState<Set<string>>(() => loadDeepDismissed());
  const [toast, setToast] = useState<{ variant: 'error' | 'success'; text: string } | null>(null);
  // Admins see the route tag in the results header; everyone else never sees
  // routing internals (they go to console.debug in dev builds only).
  const [isAdmin, setIsAdmin] = useState(false);
  // Tech cards: the admin's design switch (accordion | legacy), the one
  // accordion open at a time, and this user's bookmarks — read ONCE for the
  // whole list rather than per card.
  const cardDesign = useTechCardDesign();
  const [openCardId, setOpenCardId] = useState<string | null>(null);
  const [savedIds, setSavedIds] = useState<Set<string>>(() => new Set());
  const savedLoadedRef = useRef(false);
  // Monotonic id per runSearch call. A response is only allowed to write
  // state while its id is still the latest — otherwise a slow superseded
  // search would overwrite the results (and worse, re-apply its AI filters)
  // of the search the user actually cares about.
  const searchSeqRef = useRef(0);
  // The seq of the runSearch call in flight (0 = none). It is still in
  // flight only while it holds the token: any later claim (another search,
  // Popular, the picks, a reset) has superseded it.
  const searchInFlightSeqRef = useRef(0);
  const searchInFlight = () =>
    searchInFlightSeqRef.current !== 0 && searchInFlightSeqRef.current === searchSeqRef.current;
  // The landing effect skipped its load for that running search. Should the
  // search end with no list of its own (it failed), the landing loads then,
  // under the error, rather than leaving placeholders on screen.
  const landingDeferredRef = useRef(false);
  // State twin of searchSeqRef for things that need a re-render: restarts the
  // 5s slow-timer and remounts SearchThinking when search B starts while A is
  // still in flight (loading never flips, so `loading` alone can't signal it).
  const [searchNonce, setSearchNonce] = useState(0);
  // A search running past 5s switches the plain loader to the SearchThinking
  // animation. The network is only blamed after SearchThinking actually
  // measures it (utils/connectionCheck) — never on this timer alone.
  const slowSearch = useSlowLoadWarning(loading, 5000, searchNonce);
  const searchStartRef = useRef<number>(Date.now());
  const [error, setError] = useState<string>('');
  const [history, setHistory] = useState<DirectSearchHistoryEntry[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  // A guidance line from the server for the "nothing to search" case — e.g.
  // the filters you picked are gated behind a paid plan. Static UI copy
  // cannot know which of those applies.
  const [emptyNotice, setEmptyNotice] = useState<string>('');
  // Chips describing filters the AI layer pulled out of the query text
  // ("iot for defence trl 6" → chip "TRL 6"), mirrored into the panel.
  const [aiApplied, setAiApplied] = useState<string[]>([]);
  // Filters the AI found in the query but the server withheld because this
  // account cannot filter (Explorer, UAT S-09): chip text for the
  // "Auto-detected filter — upgrade to apply" line. Never ticked in the panel.
  const [lockedDetected, setLockedDetected] = useState<string[]>([]);
  // The last query the upgrade sheet opened by itself for, so a withheld
  // filter opens it once per query rather than on every page or re-run.
  const lockPromptQueryRef = useRef<string>('');
  // Always-current mirror of `filters` for async callbacks (reflection runs
  // after a network round-trip; the closure copy may be stale by then).
  const filtersRef = useRef<DirectSearchFilters>(filters);
  filtersRef.current = filters;
  // Set right before a PROGRAMMATIC filters write (AI reflection, history
  // replay) so the auto-apply effect skips exactly one run — those paths run
  // their search themselves; the effect exists for user checkbox toggles.
  const skipAutoApplyRef = useRef(false);
  // Values reflection itself checked, so an uncheck can be told apart from
  // "never picked" — and vetoes recorded for values the user unchecked.
  // Vetoes ride on the request as ai_exclude (the backend strips them from
  // the AI plan; re-extraction would otherwise always win the uncheck).
  // Both reset when the query text changes: new query, new extraction.
  const aiAddedRef = useRef<{ trl: Set<number>; countries: Set<string>; uniCodes: Set<string> }>(
    { trl: new Set(), countries: new Set(), uniCodes: new Set() });
  const aiVetoRef = useRef<{ trl_levels: number[]; countries: string[]; university_names: string[] }>(
    { trl_levels: [], countries: [], university_names: [] });
  const lastQueryRef = useRef<string>('');
  // Snapshot of the criteria that produced the CURRENT result set. Pagination
  // and re-runs use this — not the live filter state (which the user can edit
  // freely without immediately re-querying).
  const [appliedSearch, setAppliedSearch] = useState<{
    query: string;
    filters: DirectSearchFilters;
    backend: DirectSearchBackend;
  } | null>(null);
  const [openSection, setOpenSection] = useState<Record<string, boolean>>({
    // Only Country is open by default — the rest expand on click so the
    // sidebar isn't a wall of options on first paint.
    countries: true, trl: false, universities: false, categories: false,
  });

  // Suggestions
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionIndex, setSuggestionIndex] = useState(-1);
  // Picking a suggestion writes it into `query`, which re-arms the effect below
  // and re-opens the list the click just closed. One-shot flag, consumed by the
  // very next run of that effect.
  const justPickedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Fires once per typing-session for PostHog funnel reporting.
  const typingStartedRef = useRef(false);

  // Mobile filter drawer (only relevant on < md screens)
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
  // Narrow-viewport history drawer (visible when the right history pane
  // is hidden — i.e. < lg, which also covers cases where the browser's
  // own side panels eat the viewport width on desktop).
  const [showHistoryDrawer, setShowHistoryDrawer] = useState(false);

  // Track whether we're currently performing an auto-apply so the suggestions
  // dropdown / loading flicker stay stable while filters are toggled rapidly.
  const filtersDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A no-search list on screen: Popular, or an Explorer's picks.
  const showLandingRows = (rows: any[], tot: number, keepError?: boolean) => {
    setResults(rows);
    setTotal(tot);
    // Seed cards are a fixed sample, not a search — nothing is capped,
    // and the panel should show whole-corpus counts (TC-01).
    setTotalUncapped(tot);
    setLiveFacetCounts(null);
    setHasMoreBeyondCap(false);
    setPage(0);
    setPageSize(HITS_PER_PAGE);
    setHasSearched(false);
    setAiApplied([]);
    setLockedDetected([]);
    setAppliedSearch(null);
    setResponseMeta(null);
    setCorrectedQuery('');
    setDeepState('idle');
    quickSnapshotRef.current = null;
    if (!keepError) setError('');
  };

  // keepError: the caller has just set a message that must outlive the
  // popular load (F-21's "Enter a query…" after the box was emptied).
  const loadPopular = async (opts: { keepError?: boolean } = {}) => {
    // Popular is a state-replacing request like any search: claim the token
    // so (a) it orphans an in-flight search it is meant to supersede and
    // (b) its own late response can't clobber a search started after it.
    // Claiming orphans that search's `finally` too, so its loader is ours to
    // clear (as resetToPopular / clearFilters do) or it would spin forever.
    const seq = ++searchSeqRef.current;
    setLoading(false);
    setLoadingInline(false);
    landingDeferredRef.current = false;
    try {
      const res = await apiService.getDirectSearchPopular();
      if (seq !== searchSeqRef.current) return; // superseded — drop
      if (res.success && res.data?.results) {
        picksRowsRef.current = null;
        setPicksMeta(null);
        showLandingRows(res.data.results, res.data.total || res.data.results.length, opts.keepError);
      }
    } catch { /* ignore */ }
  };

  // The Explorer's 3 picks (onboarding v2), from the precomputed lists — never
  // through a search route, which would cap them at preview_results. Right
  // after onboarding the answer travels with the request (the save may still
  // be landing); later visits let the server read the saved choice. A failed
  // load falls back to the first 3 of Popular under the random heading.
  const loadExplorerPicks = async (opts: { keepError?: boolean } = {}) => {
    const seq = ++searchSeqRef.current;
    // An orphaned search's `finally` will not clear the loader: do it here.
    setLoading(false);
    setLoadingInline(false);
    landingDeferredRef.current = false;
    const uid = apiService.getStoredUserId?.() || '';
    const choice = landingRef.current?.choice || null;
    const first = !!choice && firstLandingRef.current !== choice.at;
    if (choice) firstLandingRef.current = choice.at;
    const seed = seedIndustryRef.current;
    // The first landing carries ob_id + first=1 so the server can match it to
    // the explore/skip just saved; a hand-off says seed=1 and is served but
    // never recorded (or unlocked) as the person's own picks.
    const q: PicksQuery = choice
      ? { industry: choice.industry, keyword: choice.keyword, outcome: choice.outcome, ob_id: choice.ob_id, first }
      : seed ? { industry: seed, seed: true } : {};
    const t0 = Date.now();
    setPicksLoading(true);
    let res: ApiResponse<PicksResponse> | null = null;
    try { res = uid ? await apiService.getOnboardingPicks(uid, q) : null; } catch { res = null; }
    if (seq !== searchSeqRef.current) return; // superseded — drop
    const data = res?.success ? res.data : undefined;
    const rows = data && data.success !== false && Array.isArray(data.results) ? data.results.slice(0, 3) : [];
    if (data && rows.length) {
      picksRowsRef.current = rows;
      setPicksMeta(data);
      showLandingRows(rows, rows.length, opts.keepError);
      setPicksLoading(false);
      const age = data.built_at ? (Date.now() - Date.parse(data.built_at)) / 3_600_000 : NaN;
      trackClick('cosmos_picks_shown', {
        mode: data.mode,
        industry: data.industry ?? null,
        keyword: data.keyword ?? null,
        tech_ids: (Array.isArray(data.ids) && data.ids.length ? data.ids : rows.map(resultTechId)).slice(0, 3),
        list_ranks: (Array.isArray(data.list_ranks) ? data.list_ranks : []).slice(0, 3),
        fallback_used: !!data.fallback?.used,
        filled_from: (data.fallback?.filled_from || []).slice(0, 4),
        build_id: data.build_id ?? null,
        list_age_h: isFinite(age) ? Math.round(age * 10) / 10 : null,
        load_ms: Date.now() - t0,
        design: cardDesign,
        first_landing: first,
        ...(choice?.ob_id ? { ob_id: choice.ob_id } : {}),
        // A hand-off's cards are not "their 3" (the funnel's Landed skips it).
        ...(q.seed ? { seed: true } : {}),
      });
      return;
    }
    trackClick('cosmos_picks_error', {
      status: res?.status ?? null,
      error: String(data?.error || res?.error || (uid ? 'no results' : 'no user')).slice(0, 120),
      fallback: 'popular',
    });
    let pop: Awaited<ReturnType<typeof apiService.getDirectSearchPopular>> | null = null;
    try { pop = await apiService.getDirectSearchPopular(); } catch { pop = null; }
    if (seq !== searchSeqRef.current) return; // superseded — drop
    const popRows = pop?.success && Array.isArray(pop.data?.results) ? pop.data!.results.slice(0, 3) : [];
    if (popRows.length) {
      // Still a landing with 3 cards (the funnel's "Landed"), flagged as the
      // browser's own fallback.
      trackClick('cosmos_picks_shown', {
        mode: 'random', industry: null, keyword: null,
        tech_ids: popRows.map(resultTechId), list_ranks: [], fallback_used: true, filled_from: ['popular'],
        build_id: null, list_age_h: null, load_ms: Date.now() - t0, design: cardDesign,
        first_landing: first, client_fallback: true,
        ...(choice?.ob_id ? { ob_id: choice.ob_id } : {}),
        ...(q.seed ? { seed: true } : {}),
      });
    }
    picksRowsRef.current = popRows;
    setPicksMeta({
      success: false, mode: 'random', industry: null, keyword: null, results: popRows,
      ids: popRows.map(resultTechId), list_ranks: [], list_key: '', build_id: null, built_at: null,
      fallback: { used: true, missing: [], filled_from: ['popular'] }, total: popRows.length,
      error: 'picks_unavailable',
    });
    showLandingRows(popRows, popRows.length, opts.keepError);
    setPicksLoading(false);
  };

  // Back to the no-search state, whichever it is for this account. Picks
  // already on screen stay (a "Clear all" on a locked panel changes nothing);
  // `force` reloads them. While it is still being decided nothing loads — the
  // landing effect does, the moment it is — but the token is still claimed,
  // so an in-flight search cannot land afterwards.
  const loadLanding = (opts: { keepError?: boolean; force?: boolean } = {}) => {
    const mode = landingModeRef.current;
    if (mode === 'wait') {
      // Orphans an in-flight search, so its loader goes too.
      searchSeqRef.current++;
      setLoading(false);
      setLoadingInline(false);
      return;
    }
    if (mode === 'picks') {
      if (!opts.force && !hasSearched && picksRowsRef.current && results === picksRowsRef.current) return;
      void loadExplorerPicks(opts);
      return;
    }
    void loadPopular(opts);
  };

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4500);
    return () => clearTimeout(t);
  }, [toast]);

  // A new result set starts with every card closed.
  useEffect(() => { setOpenCardId(null); }, [results]);

  useEffect(() => {
    if (cardDesign !== 'accordion' || savedLoadedRef.current) return;
    const uid = apiService.getStoredUserId?.();
    if (!uid) return;
    savedLoadedRef.current = true;
    apiService.getSavedCards(uid, 'technology').then(res => {
      const items: any[] = res.success && Array.isArray(res.data?.items) ? res.data.items : [];
      setSavedIds(new Set(items.filter(it => it && it.bookmarked !== false).map(it => String(it.asset_id))));
    }).catch(() => { savedLoadedRef.current = false; });
  }, [cardDesign]);

  // ---------- bootstrap ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setFacetsLoading(true);
      try {
        const res = await apiService.getDirectSearchFacets();
        if (cancelled) return;
        if (res.success && res.data?.facets) setFacets(res.data.facets);
        else setError(res.error || 'Failed to load filter values');
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load filter values');
      } finally {
        if (!cancelled) setFacetsLoading(false);
      }
    })();
    // The first list (Popular, or an Explorer's picks) is loaded by the
    // landing effect below, once it is known which one this account gets.
    (async () => {
      const uid = apiService.getStoredUserId?.();
      if (!uid) return;
      const summary = await apiService.getBillingSummary(uid);
      if (summary.success && summary.data?.success) {
        setIsAdmin(!!summary.data.is_admin);
      }
    })();
    // History source depends on whether the user is logged in:
    //   - Logged in (user_id present) → fetch from DB for cross-device persistence
    //   - Anonymous → fall back to sessionStorage (lasts the tab lifetime)
    (async () => {
      const uid = apiService.getStoredUserId?.();
      if (uid) {
        try {
          const res = await apiService.listDirectSearchHistory(uid, HISTORY_MAX);
          if (cancelled) return;
          const entries = res.success && Array.isArray(res.data?.entries)
            ? res.data!.entries.map(serverEntryToLocal)
            : loadHistoryFromSession();
          setHistory(entries);
        } catch {
          if (!cancelled) setHistory(loadHistoryFromSession());
        }
      } else {
        setHistory(loadHistoryFromSession());
      }
    })();
    keywordsCache.getKeywords().catch(() => { /* ignore */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- suggestions ----------
  useEffect(() => {
    let cancelled = false;
    const q = query.trim();
    // A locked box has nothing to suggest for (a history replay can still
    // write into it).
    if (searchLocked) {
      justPickedRef.current = false;
      setSuggestions([]); setShowSuggestions(false);
      return;
    }
    if (justPickedRef.current) {
      justPickedRef.current = false;
      setShowSuggestions(false);
      return;
    }
    if (!q) {
      setSuggestions([]); setShowSuggestions(false); return;
    }
    const t = setTimeout(async () => {
      try {
        const matches = await keywordsCache.searchKeywords(q, 12);
        if (cancelled) return;
        setSuggestions(matches);
        setShowSuggestions(matches.length > 0);
        setSuggestionIndex(-1);
      } catch { /* ignore */ }
    }, 150);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  // ---------- auto-apply filters ----------
  // Whenever the filter state changes, debounce a search so facet counts stay
  // live without the user having to hit Search. We DON'T trigger on query
  // changes — query waits for explicit Enter / Search button.
  const isFirstFilterRunRef = useRef(true);
  useEffect(() => {
    if (isFirstFilterRunRef.current) {
      // Skip the initial mount — the landing effect loads the first list.
      isFirstFilterRunRef.current = false;
      return;
    }
    if (skipAutoApplyRef.current) {
      // This change was AI-filter reflection mirroring the response we just
      // received — the results already honour it, so re-searching would only
      // burn a pipeline run (and could ping-pong forever).
      skipAutoApplyRef.current = false;
      return;
    }
    if (filtersDebounceRef.current) clearTimeout(filtersDebounceRef.current);
    filtersDebounceRef.current = setTimeout(() => {
      if (filtersAreEmpty(filters) && !query.trim()) {
        // Empty filters + empty query → revert to the popular preview state
        // (an Explorer's picks) and drop the dynamic counts so every option
        // is "available" again.
        loadLanding();
        return;
      }
      // Re-run the search but mark it as not-from-user-action so it doesn't
      // pollute history. Also reset to page 0 because the filter set changed.
      runSearch(query, filters, 0, backend, { fromUserAction: false, preserveVeto: true, internal: true });
    }, 300);
    return () => {
      if (filtersDebounceRef.current) clearTimeout(filtersDebounceRef.current);
    };
    // We intentionally only depend on filters (not query/backend) — typing
    // shouldn't fire searches; backend toggle is handled in its own onClick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // When user toggles backend (Algolia ↔ Native) re-run the active search if
  // there is one — keeps results in sync with the chosen backend.
  const skipBackendEffectRef = useRef(false);
  useEffect(() => {
    if (skipBackendEffectRef.current) {
      // History replay set the backend AND ran its own search; re-running the
      // stale appliedSearch snapshot here would race it with the old query.
      skipBackendEffectRef.current = false;
      return;
    }
    if (!appliedSearch) return;
    runSearch(appliedSearch.query, appliedSearch.filters, page, backend, { fromUserAction: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backend]);

  // ---------- Industry buckets (default taxonomy + Others) ----------
  // We pivot the backend's raw category list into the canonical
  // `DEFAULT_INDUSTRY_CATEGORIES` taxonomy declared at the top of this file,
  // plus an "Others" bucket for anything unmapped. Counts shown in the UI
  // sum across all underlying real categories that map into a given bucket.
  const otherIndustries = useMemo(() => {
    let all: string[] = [];
    const withCounts = (facets as any)?.major_categories_with_counts;
    const names = facets?.major_categories;
    if (Array.isArray(withCounts)) {
      all = withCounts.filter(x => x && typeof x.value === 'string').map(x => x.value as string);
    } else if (Array.isArray(names)) {
      all = names.filter(v => typeof v === 'string') as string[];
    }
    return computeOthersBucket(all);
  }, [facets]);

  // ---------- facet count lookup ----------
  // Counts are STATIC corpus totals from /facets (product decision 2026-08).
  // They used to be per-result-set: every search zeroed most rows and
  // reshuffled the rest, which read as "these filters are broken" — see the
  // sortAlpha note on CheckboxList for the same complaint from the other side.
  const staticCounts = useMemo(() => {
    const country: Record<string, number> = {};
    const university: Record<string, number> = {};
    const industry: Record<string, number> = {};
    const trl: Record<string, number> = {};
    if (facets) {
      (facets.countries_with_counts || []).forEach(c => { country[c.value] = c.count; });
      (facets.universities || []).forEach(u => {
        if (u && u.code) university[u.code] = u.count ?? 0;
      });
      (facets.major_categories_with_counts || []).forEach(c => { industry[c.value] = c.count; });
      let trlSum = 0;
      (facets.trl_levels || []).forEach(t => { trl[String(t.value)] = t.count; trlSum += t.count; });
      // "Not disclosed" = everything without a TRL 1-9.
      if (typeof facets.total_technologies === 'number' && facets.total_technologies > 0) {
        trl['0'] = Math.max(0, facets.total_technologies - trlSum);
      }
    }
    return { country, university, industry, trl };
  }, [facets]);

  // null until the facets payload arrives (badge hidden); 0 afterwards for a
  // value genuinely absent from the corpus.
  // Which facet_counts group each panel section reads. The backend keys are
  // Typesense/Algolia field names; the panel uses its own short names.
  const LIVE_FACET_KEY: Record<string, string> = {
    country: 'university_country',
    university: 'utech_university_id',
    industry: 'major_categories',
    trl: 'calculated_trl',
  };

  const facetCount = (attr: 'country' | 'trl' | 'university' | 'industry',
                      value: string | number): number | null => {
    if (DYNAMIC_FACET_COUNTS && liveFacetCounts) {
      const group = liveFacetCounts[LIVE_FACET_KEY[attr]];
      // 0 is a real answer here — "this option matches nothing in the current
      // result set" is exactly what TC-07 asks the panel to show.
      if (group) return group[String(value)] ?? 0;
    }
    if (!facets) return null;
    return staticCounts[attr][String(value)] ?? 0;
  };

  // ---------- AI-filter reflection ----------
  // The hybrid backend's query-understanding layer turns filter phrases in
  // the query ("trl 6", "in India", "from Stanford") into real filters and
  // applies them server-side. Mirror them into the panel so the UI tells the
  // truth about what produced the result set, and chip them above the list.
  //
  // The merge NEVER un-checks anything the user picked — it only adds. And it
  // must not re-trigger the auto-apply effect (the results already honour
  // these filters), hence skipAutoApplyRef + the same-reference short-circuit.
  const reflectAiFilters = (
    ai: DirectSearchFilters | undefined | null,
    requestFilters: DirectSearchFilters,
  ) => {
    const chips: string[] = [];
    const current = filtersRef.current;
    const fx = facetsRef.current;
    const veto = aiVetoRef.current;
    const added = aiAddedRef.current;
    const merged: DirectSearchFilters = { ...current };
    let changed = false;

    // TRL — the AI emits trl_min/trl_max (or trl_levels); the panel renders
    // multi-select levels, so expand the range into checkboxes.
    const aiLevels = aiTrlLevels(ai)
      .filter(n => !veto.trl_levels.includes(n))
      .sort((a, b) => a - b);
    if (aiLevels.length) {
      chips.push(trlChipLabel(aiLevels));
      const set = new Set(current.trl_levels || []);
      aiLevels.forEach(n => { set.add(n); added.trl.add(n); });
      if (set.size !== (current.trl_levels || []).length) {
        merged.trl_levels = Array.from(set).sort((a, b) => a - b);
        changed = true;
      }
    }

    // Countries — canonicalise casing against the facets list where possible.
    (ai?.countries || []).forEach(c => {
      const canonical = (fx?.countries || [])
        .find(fc => fc.toLowerCase() === String(c).toLowerCase()) || String(c);
      if (veto.countries.some(v => v.toLowerCase() === canonical.toLowerCase())) return;
      chips.push(canonical);
      added.countries.add(canonical);
      const cur = merged.countries || [];
      if (!cur.some(v => v.toLowerCase() === canonical.toLowerCase())) {
        merged.countries = [...cur, canonical];
        changed = true;
      }
    });

    // Universities — the AI returns names; the panel keys on codes.
    (ai?.university_names || []).forEach(name => {
      if (veto.university_names.some(v => v.toLowerCase() === String(name).toLowerCase())) return;
      chips.push(String(name));
      const uni = (fx?.universities || [])
        .find(u => (u.name || '').toLowerCase() === String(name).toLowerCase());
      if (uni?.code) {
        added.uniCodes.add(uni.code);
        const cur = merged.university_codes || [];
        if (!cur.includes(uni.code)) {
          merged.university_codes = [...cur, uni.code];
          changed = true;
        }
      }
      // No code match → chip still shows (the backend DID filter on it), the
      // checkbox just can't be ticked for a university we can't identify.
    });

    setAiApplied(chips);
    if (changed) {
      // Skip the auto-apply run ONLY when the panel still matches the filters
      // this search ran with. If the user toggled something mid-flight, that
      // intent hasn't been searched yet — the effect must fire, or the panel
      // and the results disagree with no re-search ever coming.
      skipAutoApplyRef.current = filtersEqual(filtersRef.current, requestFilters);
      setFilters(merged);
    }
  };

  // ---------- emptied box (F-21) ----------
  // `f` minus the values AI reflection ticked from the query words. Returns
  // `f` itself when there is nothing to drop, so callers can tell.
  const withoutAiAddedFilters = (f: DirectSearchFilters): DirectSearchFilters => {
    const added = aiAddedRef.current;
    const aiCountries = new Set(Array.from(added.countries).map(c => c.toLowerCase()));
    const countries = (f.countries || []).filter(c => !aiCountries.has(c.toLowerCase()));
    const uniCodes = (f.university_codes || []).filter(c => !added.uniCodes.has(c));
    const trl = (f.trl_levels || []).filter(n => !added.trl.has(Number(n)));
    if (countries.length === (f.countries || []).length
        && uniCodes.length === (f.university_codes || []).length
        && trl.length === (f.trl_levels || []).length) return f;
    return { ...f, countries, university_codes: uniCodes, trl_levels: trl };
  };

  // Back to the state a fresh page opens in (F-01): popular technologies,
  // whole-corpus panel counts, no search on record. Unlike clearFilters it
  // leaves the box alone (already empty) and drops the old result set at once
  // rather than when Popular lands, so a slow or failed Popular never leaves
  // the old matches on screen under the "Popular technologies" header.
  const resetToPopular = () => {
    if (filtersDebounceRef.current) clearTimeout(filtersDebounceRef.current);
    setLoading(false);
    setLoadingInline(false);
    setDeepState('idle');
    quickSnapshotRef.current = null;
    aiVetoRef.current = { trl_levels: [], countries: [], university_names: [] };
    aiAddedRef.current = { trl: new Set(), countries: new Set(), uniCodes: new Set() };
    lastQueryRef.current = '';
    setAppliedSearch(null);
    setHasSearched(false);
    setResults([]); setTotal(0); setTotalUncapped(0);
    setHasMoreBeyondCap(false);
    setLiveFacetCounts(null);
    setPage(0);
    setPageSize(HITS_PER_PAGE);
    setServerPages(1);
    setResponseMeta(null);
    setCorrectedQuery('');
    setEmptyNotice('');
    setAiApplied([]);
    setLockedDetected([]);
    // Claims the search token itself, which orphans any in-flight search.
    loadLanding({ keepError: true, force: true });
  };

  // ---------- run a search ----------
  const runSearch = async (
    overrideQuery?: string,
    overrideFilters?: DirectSearchFilters,
    overridePage?: number,
    overrideBackend?: DirectSearchBackend,
    options: { fromUserAction?: boolean; preserveVeto?: boolean;
               /** Re-running the search already on screen (paging, facet
                *  refresh) rather than starting a new one. */
               internal?: boolean;
               /** "Run deep search": bypass classification, use the engine. */
               forceRoute?: 'engine';
               /** "Search anyway" after a nonsense flag. */
               allowNonsense?: boolean;
               /** Ask the server for a fresh plan check (just upgraded). */
               freshEntitlements?: boolean;
               /** Keep the current list on screen (dimmed) while waiting
                *  instead of the full "Searching…" loader. */
               inline?: boolean } = {},
  ) => {
    const q = overrideQuery ?? query;
    // A new query starts a clean slate. Filters were surviving into the next
    // search because `f` falls back to live filter state, so a country ticked
    // for "solar" silently constrained the next, unrelated query while the
    // sidebar still showed it ticked. Paging and facet refreshes pass
    // `internal`, so they keep the filters that produced the current results.
    const isFreshUserQuery = !!options.fromUserAction && !options.internal
      && !!q.trim() && q.trim() !== lastQueryRef.current;
    let f = isFreshUserQuery ? {} : (overrideFilters ?? filters);
    // F-21: Search pressed on an emptied box. Filters the AI ticked from the
    // old words ("solar cells in India" → India) go with those words; the
    // ones the user ticked stay, and if any do this is a plain filters-only
    // search with them.
    const boxEmptied = !!options.fromUserAction && !options.internal
      && !options.forceRoute && !q.trim();
    if (boxEmptied) {
      const kept = withoutAiAddedFilters(f);
      if (kept !== f) {
        f = kept;
        // This path runs (or resets) itself — the [filters] effect must not
        // fire a second search, or a loadPopular that wipes the message.
        skipAutoApplyRef.current = true;
        setFilters(kept);
      }
    }
    if (isFreshUserQuery) {
      if (!filtersAreEmpty(filtersRef.current)) {
        // Clearing filters would otherwise trip the [filters] effect into
        // firing a second, duplicate search straight after this one.
        skipAutoApplyRef.current = true;
        setFilters({});
      }
      setAiApplied([]);
      setLockedDetected([]);
    }
    const p = overridePage ?? 0;
    const b = overrideBackend ?? backend;

    if (!q.trim() && filtersAreEmpty(f)) {
      // Nothing left to search. If a search is on screen, its results, counts
      // and panel numbers no longer describe anything — go back to the fresh
      // page state first. On a page that never searched, just the message.
      if (boxEmptied && (hasSearched || appliedSearch || lastQueryRef.current)) {
        resetToPopular();
      }
      setError('Enter a query or pick at least one filter to search.');
      return;
    }
    // Running a query is a paid action while the lock is on. Typing and
    // suggestions are not gated — only the moment results would appear.
    //
    // NOT limited to fromUserAction: replaying a saved search from history
    // and toggling the backend both run real queries, and gating only the
    // "user typed it" path let either one fetch a full result set. The
    // exceptions are pagination and filter auto-apply, which re-run a search
    // the user is already looking at — `options.internal` marks those. A
    // locked box (the admin switch) gates every other way in to a search
    // too, whatever can_search says.
    if ((searchLocked || !upgrade.ent.can_search) && !options.internal) {
      upgrade.gate(false, 'search', undefined, 'can_search',
        { control: 'search_bar', trigger: 'run', surface: lockSurface });
      return;
    }
    setError('');
    searchStartRef.current = Date.now();
    // Claim the latest-search token. Any response whose seq is no longer
    // current gets dropped wholesale — results, chips, filters, everything.
    const seq = ++searchSeqRef.current;
    searchInFlightSeqRef.current = seq;
    setSearchNonce(seq);
    setLoading(true);
    setLoadingInline(!!options.inline);
    setPendingPage(p);
    if (options.forceRoute === 'engine') {
      setDeepState('running');
    } else {
      // Any ordinary search replaces whatever deep result set was showing;
      // the quick page it saved is for THAT query only.
      setDeepState('idle');
      quickSnapshotRef.current = null;
    }
    setShowSuggestions(false);
    // New query text = new extraction: stale vetoes ("no TRL 6 for THAT
    // query") must not leak into a query that never mentioned TRL. But NOT on
    // auto-apply re-searches (preserveVeto): those pass the live input box,
    // which the user may have edited without submitting — wiping there would
    // erase the veto an uncheck recorded 300ms earlier, and the response
    // would re-check the box the user just cleared.
    if (!options.preserveVeto && q.trim() !== lastQueryRef.current) {
      aiVetoRef.current = { trl_levels: [], countries: [], university_names: [] };
      aiAddedRef.current = { trl: new Set(), countries: new Set(), uniCodes: new Set() };
    }
    lastQueryRef.current = q.trim();
    const veto = aiVetoRef.current;
    const aiExclude =
      veto.trl_levels.length || veto.countries.length || veto.university_names.length
        ? {
            ...(veto.trl_levels.length ? { trl_levels: [...veto.trl_levels] } : {}),
            ...(veto.countries.length ? { countries: [...veto.countries] } : {}),
            ...(veto.university_names.length ? { university_names: [...veto.university_names] } : {}),
          }
        : undefined;
    const isDeep = options.forceRoute === 'engine';
    let gotResults = false;
    try {
      trackClick('direct_search_run', { backend: b, deep: isDeep });
      const res = await apiService.directSearch(b, {
        query: q,
        filters: f,
        page: p,
        hits_per_page: HITS_PER_PAGE,
        ai_exclude: aiExclude,
        // Only ever present on a deep search — the legacy endpoint and older
        // v2 deploys keep receiving exactly the body they always did.
        ...(isDeep ? { force_route: 'engine' as const } : {}),
        ...(options.allowNonsense ? { allow_nonsense: true } : {}),
        ...(options.freshEntitlements ? { fresh_entitlements: true } : {}),
      });
      if (seq !== searchSeqRef.current) return; // superseded — drop entirely
      // res.success only says HTTP 2xx. The hybrid endpoint fails LOUDLY with
      // a 200 + {success:false, error} body when the stack is down — treating
      // that as a clean empty result set would tell the user the catalog has
      // nothing, and record a bogus 0-hit history entry on top.
      if (res.success && res.data && (res.data as any).success !== false) {
        gotResults = true;
        const hits: any[] = res.data.results || [];
        // The backend the response REALLY came from ("v2:<engine>:<route>",
        // or the legacy endpoint's method). History stores this verbatim so
        // the admin User 360 tab stops saying 'algolia' for every row.
        const realMethod = searchMethodLabel(res.data, b);
        setResults(hits);
        setTotal(res.data.total || 0);
        setEmptyNotice((res.data as any).message || '');
        setTotalUncapped(res.data.total_uncapped || res.data.total || 0);
        setLiveFacetCounts((res.data as any).facet_counts || null);
        setHasMoreBeyondCap(!!res.data.has_more_beyond_cap);
        setPage(res.data.page ?? p);
        // The engine (RAG) route returns its whole top_n in ONE page
        // (nb_pages 1); the keyword routes page at HITS_PER_PAGE. A single-page
        // answer gets a page size that covers the whole set, so the pager does
        // not promise pages 2 and 3 that hold nothing. The keyword routes keep
        // the frontend's own page math (the preview tier trims hits per page
        // server-side, and that paging UX is deliberately unchanged here).
        const tot = res.data.total || 0;
        const singlePage = Number(res.data.nb_pages) === 1 || hits.length > HITS_PER_PAGE;
        setPageSize(singlePage ? Math.max(tot, hits.length, HITS_PER_PAGE) : HITS_PER_PAGE);
        setServerPages(Number(res.data.nb_pages) || 1);
        setResponseMeta(res.data.meta || null);
        // "Showing results for battery" — only when the server searched words
        // other than the ones typed (a case or spacing change is not a fix).
        const said = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
        const corrected = typeof res.data.corrected_query === 'string' ? res.data.corrected_query.trim() : '';
        setCorrectedQuery(corrected && said(corrected) !== said(q) ? corrected : '');
        setHasSearched(true);
        setDeepState(isDeep ? 'active' : 'idle');
        if (!isDeep) quickSnapshotRef.current = null;
        // Mirror AI-extracted filters into the panel — fresh searches only.
        // Pagination reuses the same ranking, so reflecting there would be a
        // no-op at best and fight in-flight user edits at worst.
        if ((overridePage ?? 0) === 0) {
          // Filters found in the words but withheld because this account
          // cannot filter (Explorer, UAT S-09): nothing is ticked and nothing
          // reads "Applied" — the page offers the upgrade instead.
          const locked = upgrade.ent.can_filter ? [] : lockedFilterLabels(
            res.data.meta?.filters?.locked_labels,
            res.data.ai_filters_locked || res.data.meta?.filters?.locked);
          if (!locked.length) {
            reflectAiFilters(res.data.ai_filters, f);
            setLockedDetected([]);
          } else {
            setAiApplied([]);
            aiAddedRef.current = { trl: new Set(), countries: new Set(), uniCodes: new Set() };
            aiVetoRef.current = { trl_levels: [], countries: [], university_names: [] };
            setLockedDetected(locked);
            // The same sheet a locked filter tick opens, by itself once per
            // query: not again for its pages, re-runs or a deep search of it.
            const note = String(res.data.meta?.entitlement_note || '').split(',');
            const qKey = q.trim().replace(/\s+/g, ' ').toLowerCase();
            if (note.includes('filters_detected_locked') && lockPromptQueryRef.current !== qKey) {
              lockPromptQueryRef.current = qKey;
              trackClick('detected_filter_locked_auto', { labels: locked, query: q.trim() });
              upgrade.gate(false, 'full filtering', undefined, 'can_filter',
                { control: 'filter', filter: 'detected', trigger: 'auto', surface: lockSurface });
            }
          }
        }
        // Snapshot only on fresh searches (page 0). Pagination doesn't change
        // the snapshot — it just navigates within the same result set.
        if ((overridePage ?? 0) === 0) {
          setAppliedSearch({ query: q, filters: f, backend: b });
        }

        // History rule: only record when (a) this is a user-initiated *fresh*
        // search (not pagination, not history-replay, not filter toggle) AND
        // (b) the user actually typed a query — pure filter-only searches
        // don't go in history per spec. A deep search is user-initiated and
        // ran on a different backend, so it gets its own row.
        if ((overridePage ?? 0) === 0 && options.fromUserAction && q.trim()) {
          const localEntry: DirectSearchHistoryEntry = {
            // Optimistic local id; replaced with the DB id once the save
            // round-trip completes for logged-in users.
            id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            query: q,
            filters: f,
            backend: b,
            method: realMethod,
            total: res.data.total || 0,
            ts: Date.now(),
          };
          const optimistic = [localEntry, ...history].slice(0, HISTORY_MAX);
          setHistory(optimistic);

          const uid = apiService.getStoredUserId?.();
          if (uid) {
            // Persist to DB; on success replace the optimistic id with the
            // server-assigned uuid so future deletes target the right row.
            apiService.saveDirectSearchHistory(uid, {
              query: q,
              filters: f,
              backend: realMethod,
              total: res.data.total || 0,
            }).then(saveRes => {
              if (saveRes.success && saveRes.data?.entry) {
                const real = serverEntryToLocal(saveRes.data.entry);
                setHistory(prev => {
                  const idx = prev.findIndex(e => e.id === localEntry.id);
                  if (idx < 0) return prev;
                  const next = prev.slice();
                  next[idx] = real;
                  return next;
                });
              }
            }).catch(() => {
              // Save failed — fall back to sessionStorage so the entry isn't
              // lost. The optimistic entry stays visible in the right pane.
              saveHistoryToSession(optimistic);
            });
          } else {
            // Anonymous: sessionStorage only.
            saveHistoryToSession(optimistic);
          }
        }
      } else if (isDeep) {
        // A failed deep search is a failed upgrade, not a failed search: the
        // quick results stay exactly as they were, the banner reverts to the
        // offer, and the reason goes to a toast.
        setDeepState('idle');
        setToast({ variant: 'error',
          text: res.error || res.data?.error || 'Deep search failed — showing the quick results.' });
      } else {
        setError(res.error || res.data?.error || 'Search failed');
        setResults([]); setTotal(0); setTotalUncapped(0);
        setResponseMeta(null);
        setCorrectedQuery('');
        // A failed search applied nothing — stale chips from the previous
        // query would claim otherwise.
        setAiApplied([]);
        setLockedDetected([]);
      }
    } catch (e: any) {
      if (seq !== searchSeqRef.current) return; // superseded — drop entirely
      if (isDeep) {
        setDeepState('idle');
        setToast({ variant: 'error', text: e?.message || 'Deep search failed — showing the quick results.' });
      } else {
        setError(e?.message || 'Search failed');
        setResults([]); setTotal(0); setTotalUncapped(0);
        setResponseMeta(null);
        setCorrectedQuery('');
        setAiApplied([]);
        setLockedDetected([]);
      }
    } finally {
      if (searchInFlightSeqRef.current === seq) searchInFlightSeqRef.current = 0;
      // A superseded search must not clear the loader for its successor.
      if (seq === searchSeqRef.current) {
        setLoading(false);
        setLoadingInline(false);
        if (landingDeferredRef.current) {
          landingDeferredRef.current = false;
          if (!gotResults) loadLanding({ keepError: true });
        }
      }
    }
  };

  // ---------- locked search box ----------
  // Search off for this account: the box is read-only, and a click, a key
  // that would type, a paste or the Search button opens the plans sheet.
  // Never on focus — the sheet hands focus back to the box when it closes,
  // and Tab or a phone keyboard re-focus it, so a focus gate would loop.
  // Throttled: a mousedown and the keydown after it are one attempt.
  const lockAtRef = useRef(0);
  const lockSearch = (trigger: 'click' | 'type' | 'paste' | 'button') => {
    const now = Date.now();
    if (now - lockAtRef.current < 1500) return;
    lockAtRef.current = now;
    trackClick('search_locked_click', { trigger, surface: lockSurface });
    upgrade.gate(false, 'search', undefined, 'can_search',
      { control: 'search_bar', trigger, surface: lockSurface });
  };
  const lockedKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Shortcuts (copy, select all) and navigation keys stay quiet; a paste
    // has its own handler.
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === 'Enter' || e.key === 'Backspace' || e.key === 'Delete' || e.key.length === 1) {
      e.preventDefault();
      lockSearch('type');
    }
  };

  const handleQueryKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSuggestionIndex(p => (p < suggestions.length - 1 ? p + 1 : 0)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSuggestionIndex(p => (p > 0 ? p - 1 : suggestions.length - 1)); return; }
      if (e.key === 'Escape') { setShowSuggestions(false); return; }
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showSuggestions && suggestionIndex >= 0 && suggestionIndex < suggestions.length) {
        const picked = suggestions[suggestionIndex];
        if (picked !== query) justPickedRef.current = true;
        setQuery(picked); setShowSuggestions(false);
        runSearch(picked, undefined, 0, undefined, { fromUserAction: true });
      } else {
        runSearch(undefined, undefined, 0, undefined, { fromUserAction: true });
      }
    }
  };

  // ---------- filter mutators (do NOT auto-run search; only Search/Enter does) ----------
  // Un-checking a value that AI reflection itself added records a VETO: the
  // re-run re-extracts the phrase from the unchanged query text, so without
  // telling the backend to exclude it (ai_exclude), the checkbox would just
  // re-check itself after a full pipeline run — an uncheck that can't win.
  const recordVetoIfAiAdded = (key: keyof DirectSearchFilters, value: string) => {
    const added = aiAddedRef.current;
    const veto = aiVetoRef.current;
    if (key === 'countries' && added.countries.has(value)) {
      added.countries.delete(value);
      if (!veto.countries.includes(value)) veto.countries.push(value);
    } else if (key === 'university_codes' && added.uniCodes.has(value)) {
      added.uniCodes.delete(value);
      // The backend veto speaks university NAMES (that's what the AI emits).
      const name = (facetsRef.current?.universities || [])
        .find(u => u.code === value)?.name;
      if (name && !veto.university_names.includes(name)) veto.university_names.push(name);
    }
  };

  /**
   * Every filter currently applied, as a removable bubble.
   *
   * Built from `filters` rather than from what the user clicked, so a filter
   * the AI pulled out of the query text shows up here too — those were only
   * ever visible in a read-only row above the results, which meant a free user
   * could be filtered by something with no way to see or remove it.
   *
   * Labels match the sidebar exactly: raw industry categories are folded back
   * into the bucket the panel shows, university codes become names, and TRL 0
   * is "Not disclosed" rather than "TRL 0".
   */
  const activeFilterBubbles = useMemo(() => {
    const out: { id: string; group: string; label: string; ai: boolean; remove: () => void }[] = [];
    const added = aiAddedRef.current;

    (filters.countries || []).forEach(c => out.push({
      id: `country:${c}`, group: 'Country', label: c,
      ai: !!added.countries?.has?.(c),
      remove: () => removeFilter('countries', c),
    }));

    (filters.university_codes || []).forEach(code => {
      const u = (Array.isArray(facets?.universities) ? facets.universities : [])
        .find((x: any) => x?.code === code);
      out.push({
        id: `uni:${code}`, group: 'University', label: u?.name || code,
        ai: !!added.uniCodes?.has?.(code),
        remove: () => removeFilter('university_codes', code),
      });
    });

    // Industry is stored EXPANDED — one visible bucket is many raw categories —
    // so a bubble per stored value would be a wall of labels nobody recognises.
    const rawCats = filters.major_categories || [];
    if (rawCats.length) {
      const seen = new Set<string>();
      DEFAULT_INDUSTRY_CATEGORIES.forEach(bucket => {
        const expanded = expandDefaultBucket(bucket);
        const hit = expanded.filter(c => rawCats.includes(c));
        if (!hit.length) return;
        hit.forEach(c => seen.add(c));
        out.push({
          id: `ind:${bucket}`, group: 'Industry', label: bucket,
          ai: false,   // the provenance ref does not track industry
          remove: () => removeFilters('major_categories', expanded),
        });
      });
      const rest = rawCats.filter(c => !seen.has(c));
      if (rest.length) out.push({
        id: 'ind:__others__', group: 'Industry',
        label: rest.length > 1 ? `Others (${rest.length})` : rest[0],
        ai: false,
        remove: () => removeFilters('major_categories', rest),
      });
    }

    (filters.trl_levels || []).forEach(nRaw => {
      const lvl = Number(nRaw);
      out.push({
        id: `trl:${lvl}`, group: 'TRL',
        label: lvl === 0 ? 'Not disclosed' : `TRL ${lvl}`,
        ai: !!added.trl?.has?.(lvl),
        remove: () => removeTrl(lvl),
      });
    });

    return out;
  }, [filters, facets]);

  /**
   * Take a filter off. Deliberately NOT the toggle the checkboxes use: those
   * open with a paid-plan gate, so on a free account clicking × would have
   * opened the upgrade modal instead of removing the filter — and the AI layer
   * applies filters to free accounts. Removing what is already applied is
   * never a paid action.
   */
  const removeFilter = (key: keyof DirectSearchFilters, value: string) => {
    trackClick('cosmos_remove_filter', { filter: String(key), value });
    setFilters(prev => {
      const arr = ((prev[key] as string[] | undefined) || []).filter(v => v !== value);
      return { ...prev, [key]: arr };
    });
    recordVetoIfAiAdded(key, value);
  };

  const removeFilters = (key: keyof DirectSearchFilters, values: string[]) => {
    trackClick('cosmos_remove_filter', { filter: String(key), value: values.join(',') });
    const drop = new Set(values);
    setFilters(prev => {
      const arr = ((prev[key] as string[] | undefined) || []).filter(v => !drop.has(v));
      return { ...prev, [key]: arr };
    });
    values.forEach(v => recordVetoIfAiAdded(key, v));
  };

  const removeTrl = (level: number) => {
    trackClick('cosmos_remove_filter', { filter: 'trl', value: level });
    setFilters(prev => ({
      ...prev, trl_levels: (prev.trl_levels || []).filter(n => Number(n) !== level),
    }));
    const added = aiAddedRef.current;
    const veto = aiVetoRef.current;
    if (added.trl.has(level)) {
      added.trl.delete(level);
      if (!veto.trl_levels.includes(level)) veto.trl_levels.push(level);
    }
  };

  const toggleArrayFilter = (key: keyof DirectSearchFilters, value: string) => {
    if (!upgrade.ent.can_filter) {
      upgrade.gate(false, 'full filtering', undefined, 'can_filter',
        { control: 'filter', filter: FILTER_NAME[key] || String(key), surface: lockSurface });
      return;
    }
    trackClick('cosmos_apply_filter', { filter: String(key), value });
    setFilters(prev => {
      const arr = ((prev[key] as string[] | undefined) || []).slice();
      const idx = arr.indexOf(value);
      if (idx >= 0) {
        arr.splice(idx, 1);
        recordVetoIfAiAdded(key, value);
      } else {
        arr.push(value);
      }
      return { ...prev, [key]: arr };
    });
  };

  // A card's university / country link narrows the search to it. Adding
  // only: a value already applied stays applied (the chip removes it).
  const filterFromCard = (key: 'countries' | 'university_codes', value?: string) => {
    if (!value) return;
    if (((filtersRef.current[key] as string[] | undefined) || []).includes(value)) return;
    toggleArrayFilter(key, value);
  };

  const toggleTrlLevel = (level: number) => {
    if (!upgrade.ent.can_filter) {
      upgrade.gate(false, 'full filtering', undefined, 'can_filter',
        { control: 'filter', filter: 'trl', surface: lockSurface });
      return;
    }
    trackClick('cosmos_apply_filter', { filter: 'trl', value: level });
    setFilters(prev => {
      const arr = (prev.trl_levels || []).slice();
      const idx = arr.indexOf(level);
      if (idx >= 0) {
        arr.splice(idx, 1);
        const added = aiAddedRef.current;
        const veto = aiVetoRef.current;
        if (added.trl.has(level)) {
          added.trl.delete(level);
          if (!veto.trl_levels.includes(level)) veto.trl_levels.push(level);
        }
      } else {
        arr.push(level);
      }
      return { ...prev, trl_levels: arr };
    });
  };

  // Industry default-bucket toggle. Picking "Healthcare & Life Sciences" is
  // equivalent to picking ALL real categories that map to it (including the
  // canonical name itself + every reassigned-from). Picking "Others" picks
  // every category that's neither a default nor a reassignment source.
  const isOthersSelected = useMemo(() => {
    const selected = filters.major_categories || [];
    if (otherIndustries.length === 0) return false;
    return otherIndustries.every(c => selected.includes(c));
  }, [filters.major_categories, otherIndustries]);

  const isDefaultBucketSelected = (defaultCat: string): boolean => {
    const selected = filters.major_categories || [];
    const expanded = expandDefaultBucket(defaultCat);
    if (expanded.length === 0) return false;
    return expanded.every(c => selected.includes(c));
  };

  const toggleIndustry = (value: string) => {
    if (!upgrade.ent.can_filter) {
      upgrade.gate(false, 'full filtering', undefined, 'can_filter',
        { control: 'filter', filter: 'industry', surface: lockSurface });
      return;
    }
    trackClick('cosmos_apply_filter', { filter: 'industry', value });
    if (value === OTHERS_VALUE) {
      setFilters(prev => {
        const cur = (prev.major_categories || []).slice();
        if (isOthersSelected) {
          return { ...prev, major_categories: cur.filter(v => !otherIndustries.includes(v)) };
        }
        const merged = Array.from(new Set([...cur, ...otherIndustries]));
        return { ...prev, major_categories: merged };
      });
      return;
    }
    if (DEFAULT_INDUSTRY_SET.has(value)) {
      // User clicked a default bucket — toggle ALL underlying categories so
      // the backend filter still matches reassigned rows.
      const expanded = expandDefaultBucket(value);
      setFilters(prev => {
        const cur = (prev.major_categories || []).slice();
        const allOn = expanded.every(c => cur.includes(c));
        if (allOn) {
          return { ...prev, major_categories: cur.filter(c => !expanded.includes(c)) };
        }
        return { ...prev, major_categories: Array.from(new Set([...cur, ...expanded])) };
      });
      return;
    }
    // Backwards-compat: if some other code path selects a raw category, fall
    // through to the original behaviour.
    toggleArrayFilter('major_categories', value);
  };

  // A hand-off from a public tech page's Industry insights link: open with
  // that industry filter on (its chip shows under the search box). Waits for
  // the entitlements — before they load, the permissive default would let
  // the filter through for an account that cannot filter — and for the
  // landing to be decided. Filters locked: the usual upgrade prompt for
  // filtering instead, except on the Explorer landing (onboarding v2), where
  // nothing may open the sheet on entry: that industry's picks instead.
  const seedDoneRef = useRef(false);
  useEffect(() => {
    if (seedDoneRef.current || !upgrade.ready || landingMode === 'wait') return;
    seedDoneRef.current = true;
    const seed = takeCosmosSeed();
    // What's New "View new techs →": open filtered to that university. Filters
    // locked: the usual upgrade prompt — except on the Explorer landing, where
    // nothing may open the sheet on entry (the picks show as usual).
    if (seed?.university) {
      trackClick('cosmos_seed_applied', { university: seed.university, source: seed.source || null,
                                          can_filter: upgrade.ent.can_filter });
      if (upgrade.ent.can_filter) filterFromCard('university_codes', seed.university);
      else if (landingMode !== 'picks') toggleArrayFilter('university_codes', seed.university);
      return;
    }
    if (!seed?.industry) return;
    if (landingMode === 'picks') {
      // The picks speak the canonical industries; a page may carry a raw one.
      const industry = DEFAULT_INDUSTRY_SET.has(seed.industry)
        ? seed.industry : (INDUSTRY_REASSIGNMENT[seed.industry] || seed.industry);
      seedIndustryRef.current = industry;
      trackClick('cosmos_seed_applied', {
        industry: seed.industry, source: seed.source || null, can_filter: upgrade.ent.can_filter,
        // A fresh onboarding answer wins over the hand-off.
        applied_as: landing?.choice ? 'ignored_onboarding_choice' : 'picks',
      });
      return;
    }
    trackClick('cosmos_seed_applied', { industry: seed.industry, source: seed.source || null, can_filter: upgrade.ent.can_filter });
    toggleIndustry(seed.industry);
    // toggleIndustry is recreated each render; the seed is read once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upgrade.ready, landingMode]);

  // ---------- the no-search landing ----------
  // Loads the first list once it is known which one this account gets, and
  // again when that changes: a trial started from the picks' block (paid →
  // Popular, no reload), or the onboarding answer arriving (the first
  // landing). A search on screen is left alone — emptying the box comes back
  // through loadLanding. Declared after the seed effect so a hand-off is read
  // before the first picks request.
  const choiceAt = landing?.choice?.at || 0;
  const lastLandingRef = useRef<{ mode: string; at: number } | null>(null);
  useEffect(() => {
    if (landingMode === 'wait') return;
    const prev = lastLandingRef.current;
    lastLandingRef.current = { mode: landingMode, at: choiceAt };
    // Same list as before and still on screen: Popular does not reshuffle
    // because an answer arrived (a paying user re-opening their interests);
    // picks reload only for a new answer.
    const sameList = !!prev && prev.mode === landingMode && (landingMode === 'popular' || prev.at === choiceAt);
    const onScreen = landingMode === 'picks'
      ? !!picksRowsRef.current && results === picksRowsRef.current
      : results.length > 0;
    if (sameList && onScreen) return;
    // A search on screen, or one still running — typed during the skeleton
    // while the landing was being decided — is the user's; loading the
    // landing now would take its token and drop its answer.
    if (hasSearched) return;
    if (searchInFlight()) { landingDeferredRef.current = true; return; }
    if (landingMode === 'picks') void loadExplorerPicks();
    else void loadPopular();
    // The loaders read refs and setters; the mode and the answer are the triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landingMode, choiceAt]);

  const clearFilters = () => {
    // Orphan any in-flight search: its late response must not resurrect the
    // filters/chips/results the user just wiped.
    searchSeqRef.current++;
    setLoading(false);
    setLoadingInline(false);
    setDeepState('idle');
    quickSnapshotRef.current = null;
    aiVetoRef.current = { trl_levels: [], countries: [], university_names: [] };
    aiAddedRef.current = { trl: new Set(), countries: new Set(), uniCodes: new Set() };
    lastQueryRef.current = '';
    setFilters({});
    setQuery('');
    setAiApplied([]);
    setLockedDetected([]);
    // The auto-apply effect will see empty filters + empty query and call
    // loadLanding(), but trigger it directly here too so the user sees the
    // reset immediately even if the debounce hasn't fired.
    if (filtersDebounceRef.current) clearTimeout(filtersDebounceRef.current);
    loadLanding();
  };

  // Pagination — capped to MAX_PAGES. Uses appliedSearch snapshot so changing
  // filters mid-pagination doesn't break navigation.
  // Prefer the server's own count: it knows how many results it kept, while
  // total/pageSize here is a guess that disagrees whenever the two disagree.
  const totalPages = Math.min(
    MAX_PAGES,
    Math.max(1, Number(serverPages) || Math.ceil(total / pageSize)));
  const goToPage = (p: number) => {
    if (p < 0 || p >= totalPages) return;
    if (!appliedSearch) return;
    // Pages of a query are sliced from the server's result cache, so the
    // answer is near-instant: keep the list on screen with a light inline
    // indicator rather than flashing the full "Searching…" state. Should the
    // cache have expired and the wait run long, the slow-search timer still
    // promotes it to the full loader.
    runSearch(appliedSearch.query, appliedSearch.filters, p, appliedSearch.backend,
      { fromUserAction: false, internal: true, inline: true });
  };

  // ---------- deep search ----------
  // Offered by the server (meta.deep_search_offered) after a simple-route
  // search when the admin setting is on. Re-runs the SAME query + filters
  // with force_route 'engine' so the RAG engine reads the results with AI.
  const deepOfferKeyNow = appliedSearch ? deepOfferKey(appliedSearch.query, appliedSearch.filters) : '';
  const deepOffered = deepState === 'idle' && hasSearched && !!appliedSearch
    && responseMeta?.deep_search_offered === true
    && !deepDismissed.has(deepOfferKeyNow);

  const runDeepSearch = () => {
    if (!appliedSearch || loading) return;
    quickSnapshotRef.current = {
      results, total, totalUncapped, hasMoreBeyondCap, liveFacetCounts,
      page, pageSize, meta: responseMeta, aiApplied, lockedDetected, emptyNotice, correctedQuery,
    };
    trackClick('deep_search_run', {
      query: appliedSearch.query, backend: appliedSearch.backend,
      route: responseMeta?.route || null, engine: responseMeta?.engine || null,
    });
    runSearch(appliedSearch.query, appliedSearch.filters, 0, appliedSearch.backend,
      { fromUserAction: true, preserveVeto: true, forceRoute: 'engine', inline: true });
  };

  // An Explorer who starts a trial or buys a plan from the sheet stays on
  // this page: re-run the search on screen, with a fresh plan check, so the
  // filters found in the words now apply (the server remembers the old
  // decision for a while otherwise).
  useEffect(() => {
    if (!upgrade.ent.can_filter || !lockedDetected.length || !appliedSearch || loading) return;
    setLockedDetected([]);
    runSearch(appliedSearch.query, appliedSearch.filters, 0, appliedSearch.backend,
      { fromUserAction: false, preserveVeto: true, internal: true, freshEntitlements: true });
    // A search still in flight when the plan changed finishes first (it may
    // set the row from the old plan); `loading` brings this back for it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upgrade.ent.can_filter, loading]);

  const dismissDeepOffer = () => {
    if (!appliedSearch) return;
    const key = deepOfferKeyNow;
    setDeepDismissed(prev => {
      const next = new Set(prev);
      next.add(key);
      saveDeepDismissed(next);
      return next;
    });
    trackClick('deep_search_dismiss', { query: appliedSearch.query, route: responseMeta?.route || null });
  };

  // Puts the quick page back from the snapshot — no request, no loader.
  const restoreQuickResults = () => {
    const snap = quickSnapshotRef.current;
    if (!snap) return;
    trackClick('deep_search_back', { query: appliedSearch?.query || '' });
    setResults(snap.results);
    setTotal(snap.total);
    setTotalUncapped(snap.totalUncapped);
    setHasMoreBeyondCap(snap.hasMoreBeyondCap);
    setLiveFacetCounts(snap.liveFacetCounts);
    setPage(snap.page);
    setPageSize(snap.pageSize);
    setResponseMeta(snap.meta);
    setAiApplied(snap.aiApplied);
    setLockedDetected(snap.lockedDetected);
    setEmptyNotice(snap.emptyNotice);
    setCorrectedQuery(snap.correctedQuery);
    setDeepState('idle');
    quickSnapshotRef.current = null;
  };

  // ---------- history management ----------
  // Optimistic delete: remove from local state immediately, fire-and-forget
  // the DB delete for logged-in users. For anonymous users we just persist
  // the new list to sessionStorage.
  const deleteHistoryEntry = (entryId: string) => {
    const next = history.filter(e => e.id !== entryId);
    setHistory(next);
    const uid = apiService.getStoredUserId?.();
    if (uid && !entryId.startsWith('local-')) {
      apiService.deleteDirectSearchHistoryEntry(uid, entryId).catch(() => { /* tolerate */ });
    } else {
      saveHistoryToSession(next);
    }
  };

  const clearAllHistory = () => {
    if (history.length === 0) return;
    if (!window.confirm('Clear your entire Direct Search history?')) return;
    setHistory([]);
    const uid = apiService.getStoredUserId?.();
    if (uid) {
      apiService.clearDirectSearchHistory(uid).catch(() => { /* tolerate */ });
    }
    // Always wipe sessionStorage too (covers anonymous + safety net).
    saveHistoryToSession([]);
  };

  // Desktop sidebar collapse state. Code-managed so the collapsed state
  // persists across re-renders but resets on full page reload (per spec).
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [historyCollapsed, setHistoryCollapsed] = useState(false);

  // History body shared by the desktop side aside and the mobile drawer.
  // `onPick` lets the drawer close itself after a history entry is replayed.
  const renderHistoryBody = (opts: { hideHeader?: boolean; onPick?: () => void } = {}) => (
    <>
      {!opts.hideHeader && (
        <div className="flex items-center justify-between gap-2 mb-3 flex-nowrap">
          <h2 className="text-sm font-semibold text-grayscale-1300 whitespace-nowrap">Search history</h2>
          {history.length > 0 && (
            <button
              onClick={clearAllHistory}
              className="text-[11px] whitespace-nowrap shrink-0 text-grayscale-1100 hover:text-red-600 hover:underline"
              title="Clear search history"
            >Clear all</button>
          )}
        </div>
      )}
      {history.length === 0 && (
        <div className="text-xs text-grayscale-1100">Past searches will show up here.</div>
      )}
      <ul className="space-y-2">
        {history.map(h => (
          <li key={h.id} className="group relative">
            <button
              onClick={() => {
                // Close the suggestion list and keep it closed. Writing to
                // `query` re-arms the [query] effect, which re-opens the list
                // over the results the replay is about to load. Only flag it
                // when the value actually changes: re-picking the entry that
                // is already in the box is a no-op set, the effect never runs,
                // and the flag would survive to eat the next real dropdown.
                setShowSuggestions(false);
                if (h.query !== query) justPickedRef.current = true;
                setQuery(h.query);
                // Programmatic restore: replay runs its own search below, so
                // both reactive effects must sit this one out — the [filters]
                // effect would fire a 300ms-later duplicate, and the [backend]
                // effect would re-run the STALE appliedSearch on the new
                // backend, racing the replay with the previous query.
                skipAutoApplyRef.current = true;
                // Fresh object on purpose: replaying the SAME entry twice
                // must still fire the [filters] effect (identity deps) so the
                // skip flag above is consumed, not leaked to swallow the
                // user's next real toggle.
                setFilters({ ...(h.filters || {}) });
                if (h.backend !== backend) skipBackendEffectRef.current = true;
                setBackend(h.backend);
                runSearch(h.query, h.filters || {}, 0, h.backend, { fromUserAction: false });
                opts.onPick?.();
              }}
              className="w-full text-left p-2 pr-8 border border-grayscale-200 rounded-lg hover:bg-grayscale-50"
            >
              <div className="text-sm text-grayscale-1300 truncate font-medium pr-5">
                {h.query || '(filter only)'}
              </div>
              {/* Truncate: a long filter summary wrapped to three or four lines
                  and made every row a different height. */}
              <div className="text-[11px] text-grayscale-1100 mt-0.5 truncate">
                {h.total.toLocaleString()} hits
                {summarizeFilters(h.filters || {}) ? ` · ${summarizeFilters(h.filters || {})}` : ''}
                {h.method && h.method.endsWith(':deep') ? ' · deep search' : ''}
              </div>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); deleteHistoryEntry(h.id); }}
              className="absolute top-1.5 right-1.5 text-grayscale-700 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity w-6 h-6 flex items-center justify-center rounded hover:bg-grayscale-100"
              title="Remove from history"
              aria-label="Remove from history"
            >×</button>
          </li>
        ))}
      </ul>
    </>
  );

  // ---------- render ----------
  // Filters body shared by the desktop sidebar and the mobile drawer.
  // `hideHeader` is set true in the mobile drawer (which already paints its
  // own header) so we don't render "Filters" twice.
  const filterCardOn = useFilterCardOn();
  const searchButtonOn = useSearchButtonOn();
  const renderFilters = (opts: { hideHeader?: boolean } = {}) => (
    <div>
      {!opts.hideHeader && (
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-grayscale-1300">Filters</h2>
          <button onClick={clearFilters} className="text-xs text-primary-600 hover:underline">Clear all</button>
        </div>
      )}
      {facetsLoading && <div className="text-xs text-grayscale-1100">Loading filters…</div>}
      {upgrade.ent.can_filter && <TrialUpgradeCard />}
      {!upgrade.ent.can_filter && filterCardOn && <FilterUpgradeCard />}
      {!upgrade.ent.can_filter && !filterCardOn && (
        <button onClick={() => upgrade.gate(false, 'full filtering', undefined, 'can_filter',
          { control: 'filter', filter: 'panel', surface: lockSurface })}
          className="flex items-center gap-1.5 text-xs text-primary-700 mb-2">
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="1.8" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.8" />
          </svg>
          Full filtering is a paid feature
        </button>
      )}
      {facets && (
        <div className="space-y-3">
              {/* Country */}
              <FilterSection title="Country" isOpen={openSection.countries}
                onToggle={() => setOpenSection(s => ({ ...s, countries: !s.countries }))}>
                <CheckboxList
                  options={(Array.isArray(facets.countries) ? facets.countries : []).map(c => ({
                    value: c,
                    label: c,
                    count: facetCount('country', c),
                  }))}
                  selected={filters.countries || []}
                  onToggle={v => toggleArrayFilter('countries', v)}
                  sortAlpha
                />
              </FilterSection>

              {/* TRL — short labels only (TRL 1, TRL 2, …). The full
                  definition is intentionally dropped per product spec; users
                  who need the description can hover the chip in results. */}
              <FilterSection title="TRL" isOpen={openSection.trl}
                onToggle={() => setOpenSection(s => ({ ...s, trl: !s.trl }))}>
                <CheckboxList
                  preserveOrder
                  options={(() => {
                    const numbered = [1,2,3,4,5,6,7,8,9].map(n => ({
                      value: String(n),
                      label: `TRL ${n}`,
                      count: facetCount('trl', n),
                    }));
                    return [
                      ...numbered,
                      {
                        value: '0',
                        label: 'Not disclosed',
                        count: facetCount('trl', 0),
                      },
                    ];
                  })()}
                  selected={(filters.trl_levels || []).map(String)}
                  onToggle={v => toggleTrlLevel(Number(v))}
                />
              </FilterSection>

              {/* University — country dropped from the label per design.
                  Allow text wrap so long names like "Indian Institute of
                  Technology Bombay" don't truncate in the (now-narrower) sidebar.
                  Not shown at all when the server sends no universities (an
                  Explorer is not given the list). */}
              {Array.isArray(facets.universities) && facets.universities.length > 0 && (
              <FilterSection title="University" isOpen={openSection.universities}
                onToggle={() => setOpenSection(s => ({ ...s, universities: !s.universities }))}>
                <CheckboxList
                  options={(Array.isArray(facets.universities) ? facets.universities : [])
                    .filter(u => u && typeof u.code === 'string')
                    .map(u => ({
                      value: u.code,
                      label: u.name || u.code,
                      count: facetCount('university', u.code),
                      // Short-form aliases let users find a school by typing
                      // "IIT Bombay" / "MIT" / "UCLA" even though the canonical
                      // label is the full name.
                      searchAliases: UNIVERSITY_ALIASES[u.name || ''] || [],
                    }))}
                  selected={filters.university_codes || []}
                  onToggle={v => toggleArrayFilter('university_codes', v)}
                  searchable
                  wrap
                  sortAlpha
                />
              </FilterSection>
              )}

              {/* Industry: canonical default taxonomy + Others bucket. Counts
                  are aggregated across the underlying real categories so a
                  user picking "Healthcare & Life Sciences" sees the combined
                  count for healthcare + biotech + medtech etc.

                  Sorted alphabetically per spec — defaults A→Z first, then
                  the "Others" catch-all is appended at the bottom (out of
                  alpha order so it doesn't get lost mid-list). */}
              <FilterSection title="Industry" isOpen={openSection.categories}
                onToggle={() => setOpenSection(s => ({ ...s, categories: !s.categories }))}>
                <CheckboxList
                  preserveOrder
                  options={(() => {
                    const defaultOptions = DEFAULT_INDUSTRY_CATEGORIES.map(defaultCat => {
                      const expanded = expandDefaultBucket(defaultCat);
                      const aggregate = facets
                        ? expanded.reduce((sum, c) => sum + (facetCount('industry', c) ?? 0), 0)
                        : null;
                      return { value: defaultCat, label: defaultCat, count: aggregate };
                    }).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

                    const othersOption = otherIndustries.length > 0 ? [{
                      value: OTHERS_VALUE,
                      label: `Others (${otherIndustries.length})`,
                      count: facets
                        ? otherIndustries.reduce((sum, c) => sum + (facetCount('industry', c) ?? 0), 0)
                        : null,
                    }] : [];

                    return [...defaultOptions, ...othersOption];
                  })()}
                  selected={(() => {
                    const sel: string[] = [];
                    DEFAULT_INDUSTRY_CATEGORIES.forEach(defaultCat => {
                      if (isDefaultBucketSelected(defaultCat)) sel.push(defaultCat);
                    });
                    if (isOthersSelected) sel.push(OTHERS_VALUE);
                    return sel;
                  })()}
                  onToggle={toggleIndustry}
                />
              </FilterSection>
            </div>
          )}
    </div>
  );

  // Re-usable Invite / Feedback / Bug strip. Rendered in two slots so the
  // page no longer needs a dedicated header row: at the top of the desktop
  // history pane, and inside the history drawer on narrow viewports.
  const renderActionButtons = (orientation: 'row' | 'col' = 'row') =>
    openModal ? (
      <div className={`flex ${orientation === 'row' ? 'flex-row' : 'flex-row'} items-center justify-around gap-1 sm:gap-2`}>
        <button
          onClick={() => { trackClick('direct_search_click_invite'); openModal(ModalType.Invite); }}
          className="flex flex-col justify-center items-center gap-0.5 px-2 py-1 rounded-lg hover:bg-grayscale-100 transition-colors"
          title="Invite"
        >
          <InviteIcon />
          <span className="text-[10px] text-grayscale-1100 leading-none">Invite</span>
        </button>
        <button
          onClick={() => { trackClick('direct_search_click_feedback'); openModal(ModalType.Feedback); }}
          className="flex flex-col justify-center items-center gap-0.5 px-2 py-1 rounded-lg hover:bg-grayscale-100 transition-colors"
          title="Feedback"
        >
          <FeedbackIcon />
          <span className="text-[10px] text-grayscale-1100 leading-none">Feedback</span>
        </button>
        <button
          onClick={() => { trackClick('direct_search_click_bug'); openModal(ModalType.Bug); }}
          className="flex flex-col justify-center items-center gap-0.5 px-2 py-1 rounded-lg hover:bg-grayscale-100 transition-colors"
          title="Bug"
        >
          <BugIcon />
          <span className="text-[10px] text-grayscale-1100 leading-none">Bug</span>
        </button>
      </div>
    ) : null;

  // Which loading presentation the results pane shows. A fresh search swaps
  // the list for the full loader; an inline load (page from the cache, deep
  // search) keeps it on screen dimmed. A page that unexpectedly runs past the
  // slow threshold is promoted to the full loader — a deep search never is,
  // because the banner is already telling the user why it takes a while.
  const showFullLoader = loading && (!loadingInline || (slowSearch && deepState !== 'running'));
  const inlineBusy = loading && !showFullLoader;
  const showDeepBanner = deepState !== 'idle' || (deepOffered && !showFullLoader);
  // The words the browser's own keyword score reads when the server sent no
  // score: the corrected ones if a typo was fixed, never the misspelling.
  const relevanceQuery = hasSearched ? (correctedQuery || appliedSearch?.query || '') : '';
  // The no-search area is the Explorer landing (or its placeholder) rather
  // than Popular: the 3 picks + the upgrade block, and nothing below them —
  // no empty-state line, no pager, no deep-search strip.
  const landingView = !hasSearched && landingMode !== 'popular';
  const picksCards: PickCard[] = useMemo(() => {
    if (!picksRowsRef.current || results !== picksRowsRef.current) return [];
    return results.map((r: any) => {
      const m = r?.metadata || {};
      return {
        techId: resultTechId(r),
        universityId: m.utech_university_id ? String(m.utech_university_id) : undefined,
        preview: {
          title: m.utech_title || m.title || 'Untitled',
          university: m.university_name || '',
          country: m.university_country || '',
          trl: m.calculated_trl,
          summary: m.short_summary || firstSentence(m.utech_summary || ''),
        },
      };
    }).filter(c => c.techId);
  }, [results]);
  const picksMode: PicksViewMode = picksMeta?.mode === 'industry_keyword' || picksMeta?.mode === 'industry'
    ? picksMeta.mode : 'random';

  return (
    <main className="flex flex-1 flex-col self-stretch rounded-2xl bg-white shadow-custom-light overflow-hidden">
      <FloatingToast variant={toast?.variant} message={toast?.text} visible={!!toast} />
      {/* The dedicated header row was removed per design — it left a visible
          empty band above the filter/search area. Action buttons (Invite /
          Feedback / Bug) now live at the top of the history pane on desktop
          and inside the history drawer on mobile. Mobile Filters/History
          toggles live in a tight strip just above the search input. */}

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Desktop filter sidebar — collapsible. When collapsed the panel
            shrinks to a thin rail with a single expand button so the
            results get the entire screen width. */}
        {sidebarCollapsed ? (
          <aside className="hidden md:flex shrink-0 w-10 border-r border-grayscale-200 bg-grayscale-50 items-start justify-center pt-3">
            <button
              onClick={() => setSidebarCollapsed(false)}
              className="w-8 h-10 flex items-center justify-center text-grayscale-1200 hover:text-grayscale-1300 hover:bg-grayscale-100 rounded transition-colors"
              title="Expand filters"
              aria-label="Expand filters"
            >
              {/* right-chevron — points where the panel will expand to */}
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </aside>
        ) : (
          <aside className="hidden md:block w-[17rem] lg:w-[19rem] border-r border-grayscale-200 overflow-y-auto p-4 bg-grayscale-50 shrink-0">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-grayscale-1300">Filters</h2>
              <div className="flex items-center gap-2">
                <button onClick={clearFilters} className="text-xs text-primary-600 hover:underline">Clear all</button>
                <button
                  onClick={() => setSidebarCollapsed(true)}
                  className="text-grayscale-1200 hover:text-grayscale-1300 w-8 h-8 flex items-center justify-center rounded hover:bg-grayscale-100 transition-colors"
                  title="Collapse filters"
                  aria-label="Collapse filters"
                >
                  {/* left-chevron */}
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
            {renderFilters({ hideHeader: true })}
          </aside>
        )}

        {/* Mobile filter drawer — slides in from the LEFT. The drawer +
            backdrop together cover the full viewport with solid white so
            no part of the underlying page (the mobile "Cosmos" header,
            the side nav, etc.) bleeds through. z-[60] is one layer above
            the mobile sidebar's z-40 so we always paint on top. */}
        {showFiltersDrawer && (
          <div className="fixed inset-0 z-[60] md:hidden flex bg-white">
            {/* Drawer occupies the full viewport — no visible edge or
                shadow seam between the panel and the surrounding area. */}
            <aside className="relative h-full w-full bg-white overflow-y-auto p-4 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold text-grayscale-1300">Filters</h2>
                <div className="flex items-center gap-3">
                  <button onClick={clearFilters} className="text-xs text-primary-600 hover:underline">Clear all</button>
                  <button onClick={() => setShowFiltersDrawer(false)}
                    className="text-grayscale-1100 hover:text-grayscale-1300 text-xl leading-none w-7 h-7 flex items-center justify-center" aria-label="Close filters">×</button>
                </div>
              </div>
              <div className="flex-1">
                {renderFilters({ hideHeader: true })}
              </div>
              <button
                onClick={() => setShowFiltersDrawer(false)}
                className="mt-3 w-full px-4 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium"
              >Done</button>
            </aside>
          </div>
        )}

        {/* Main column */}
        <section className="flex-1 flex flex-col min-w-0">
          <div className="p-4 border-b border-grayscale-200 relative">
            {/* Unpaid accounts only (admin → Billing → Upgrade prompts). */}
            <UpgradeBanner />
            {/* Mobile Filters / History toggles. The desktop header that used
                to hold these was removed to kill the empty band at the top —
                so we surface them inline above the search input on narrow
                viewports where the side panels are hidden. */}
            <div className="flex items-center gap-2 mb-3 lg:hidden">
              <button
                onClick={() => setShowFiltersDrawer(true)}
                className="md:hidden px-3 py-1.5 text-sm border border-grayscale-300 rounded-lg flex items-center gap-1"
              >
                <span>Filters</span>
                {!filtersAreEmpty(filters) && (
                  <span className="bg-primary-500 text-white text-[10px] rounded-full px-1.5 py-0.5">on</span>
                )}
              </button>
              <button
                onClick={() => setShowHistoryDrawer(true)}
                className="px-3 py-1.5 text-sm border border-grayscale-300 rounded-lg flex items-center gap-1"
                aria-label="Open search history"
              >
                <span>History</span>
                {history.length > 0 && (
                  // Fixed-size square + rounded-full = perfect circle, instead
                  // of padding-based pill which stretches into an oval.
                  <span className="bg-primary-500 text-white text-[10px] font-medium rounded-full w-5 h-5 inline-flex items-center justify-center">{history.length}</span>
                )}
              </button>
              {/* Unpaid accounts only (admin → Billing → Upgrade prompts). On
                  the Explorer landing it sits beside the search box from sm
                  up, so this one is the phone's. */}
              <span className={explorerView ? 'ml-auto sm:hidden' : 'ml-auto'}>
                <UpgradeSearchButton variant="toolbar" surface={explorerView ? 'cosmos_picks' : undefined} />
              </span>
            </div>
            <div className="flex items-start gap-2">
            <div className="relative flex-1 min-w-0">
              {searchLocked && (
                <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-grayscale-1000" aria-hidden="true">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.8" />
                  </svg>
                </span>
              )}
              <input
                ref={inputRef}
                type="text"
                value={query}
                readOnly={searchLocked}
                aria-describedby={searchLocked ? 'direct-search-locked-note' : undefined}
                onMouseDown={searchLocked ? () => lockSearch('click') : undefined}
                onPaste={searchLocked ? (e => { e.preventDefault(); lockSearch('paste'); }) : undefined}
                onChange={e => {
                  const v = e.target.value;
                  setQuery(v);
                  if (v.length > 0 && !typingStartedRef.current) {
                    typingStartedRef.current = true;
                    trackClick('direct_search_typing_started');
                  }
                  // Real search intent, captured BEFORE the paywall fires: the
                  // paywall only raises on submit, so a free user who types a
                  // query and then thinks better of it is invisible without
                  // this. Three characters is the threshold where a stray
                  // keypress becomes an actual query. Fires once per query;
                  // clearing the box arms it again.
                  if (v.trim().length >= 3 && !searchIntentRef.current) {
                    searchIntentRef.current = true;
                    trackClick('search_intent_typed', {
                      length: v.trim().length,
                      locked: !upgrade.ent.can_search,
                      plan: upgrade.ent.plan || null,
                    });
                  }
                  if (v.length === 0) {
                    typingStartedRef.current = false;
                    searchIntentRef.current = false;
                  }
                }}
                onKeyDown={searchLocked ? lockedKeyDown : handleQueryKeyDown}
                onFocus={() => {
                  trackClick('direct_search_input_focus');
                  if (suggestions.length > 0) setShowSuggestions(true);
                }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                placeholder="Search by keyword or problem"
                className={`w-full px-4 py-3 pr-32 rounded-xl border border-grayscale-300 focus:border-primary-400 focus:outline-none text-grayscale-1300${
                  searchLocked ? ' pl-10 cursor-pointer' : ''}`}
              />
              {searchLocked && (
                <span id="direct-search-locked-note" className="sr-only">Search is included with Voyager.</span>
              )}
              <button
                // Locked: the sheet first, before the empty-box message — an
                // Explorer pressing Search on an empty box is asking to search.
                onClick={() => (searchLocked
                  ? lockSearch('button')
                  : runSearch(undefined, undefined, 0, undefined, { fromUserAction: true }))}
                disabled={loading}
                title={searchLocked ? 'Search is included with Voyager' : undefined}
                className={`absolute right-2 top-1/2 -translate-y-1/2 px-4 py-1.5 text-white rounded-lg disabled:opacity-50 text-sm ${
                  searchLocked ? 'bg-primary-300 hover:bg-primary-400' : 'bg-primary-500 hover:bg-primary-600'}`}
              >{loading && !loadingInline ? 'Searching…' : 'Search'}</button>
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute left-0 right-0 mt-1 bg-white border border-grayscale-300 rounded-lg shadow-lg z-30 max-h-72 overflow-y-auto">
                  {suggestions.map((s, i) => (
                    <button key={s + i}
                      onMouseDown={e => {
                        e.preventDefault();
                        trackClick('direct_search_suggestion_click', { keyword: s, position: i });
                        if (s !== query) justPickedRef.current = true;
                        setQuery(s);
                        setShowSuggestions(false);
                        runSearch(s, undefined, 0, undefined, { fromUserAction: true });
                      }}
                      onMouseEnter={() => setSuggestionIndex(i)}
                      className={`block w-full text-left px-3 py-2 text-sm ${i === suggestionIndex ? 'bg-primary-100' : 'hover:bg-grayscale-100'}`}
                    >{s}</button>
                  ))}
                </div>
              )}
            </div>
            {/* The Explorer landing: "Upgrade to Voyager" right beside the
                box (sm and up; the phone's is in the row above). */}
            {explorerView && (
              <span className="hidden sm:block shrink-0">
                <UpgradeSearchButton variant="inline" surface="cosmos_picks" />
              </span>
            )}
            </div>
            {/* Applied filters, right under the box that produced them. The
                sidebar is collapsed on narrow screens and scrolled away on
                long result lists, so this is often the only place a person can
                see what is narrowing their results. */}
            {activeFilterBubbles.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {activeFilterBubbles.map(b => (
                  <span key={b.id}
                    title={`${b.group}: ${b.label}${b.ai ? ' — picked up from your query' : ''}`}
                    className="inline-flex items-center gap-1 rounded-full border border-primary-300
                      bg-primary-50 pl-2.5 pr-1 py-0.5 text-xs text-grayscale-1300 max-w-full">
                    {b.ai && <span aria-hidden className="opacity-70">✨</span>}
                    <span className="text-grayscale-1100">{b.group}</span>
                    <span className="font-medium truncate max-w-[14rem]">{b.label}</span>
                    <button type="button" onClick={b.remove}
                      aria-label={`Remove ${b.group} filter ${b.label}`}
                      className="w-4 h-4 rounded-full hover:bg-primary-200 text-grayscale-1100 leading-none">
                      ×
                    </button>
                  </span>
                ))}
                {activeFilterBubbles.length > 1 && (
                  <button type="button" onClick={clearFilters}
                    className="text-xs text-grayscale-1100 underline underline-offset-2 ml-1">
                    Clear all
                  </button>
                )}
              </div>
            )}
            {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {showFullLoader && (
              slowSearch ? (
                // Past 5s: witty animated loader. It measures the connection
                // itself and only mentions the network when it's actually slow.
                <SearchThinking key={searchNonce} startedAt={searchStartRef.current} />
              ) : (
                <div className="flex items-center gap-2 text-sm text-grayscale-1100">
                  <Spinner className="h-4 w-4" />
                  Searching…
                </div>
              )
            )}
            {/* Deep search — offer / progress / result status. A compact strip
                above the list, never a modal: the quick results stay usable
                (dimmed while the deep run is in flight). */}
            {/* The query looked like keyboard mash AND matched nothing. Said
                without accusing anyone — a paste can go wrong — and always with
                a way through, because the detector can be wrong about an
                institution the catalogue has never indexed. */}
            {responseMeta?.nonsense?.flagged && (
              <div
                data-testid="nonsense-notice"
                className="mb-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-grayscale-1200 flex flex-wrap items-center gap-x-3 gap-y-2"
              >
                <span>
                  <span className="font-medium text-grayscale-1300">
                    That search didn&apos;t look like a topic we could match.
                  </span>{' '}
                  Try a technology, field or university name — or search anyway.
                </span>
                <button
                  type="button"
                  onClick={() => {
                    trackClick('nonsense_search_anyway', { query });
                    runSearch(query, filters, 0, backend,
                      { fromUserAction: true, allowNonsense: true });
                  }}
                  className="rounded-lg bg-white px-2.5 py-1 font-medium text-primary-700 border border-primary-200 hover:bg-primary-50"
                >
                  Search anyway
                </button>
                <span className="text-grayscale-1100">
                  You may see results that aren&apos;t relevant.
                </span>
              </div>
            )}
            {showDeepBanner && (
              <div
                data-testid="deep-search-banner"
                className="mb-3 rounded-xl border border-primary-200 bg-primary-50/60 px-3 py-2 text-xs text-grayscale-1200 flex flex-wrap items-center gap-x-3 gap-y-2"
              >
                {deepState === 'running' ? (
                  <div role="status" aria-live="polite" className="flex items-center gap-2">
                    <Spinner className="h-3.5 w-3.5" />
                    <span>Deep search running… reading the top results with AI</span>
                  </div>
                ) : deepState === 'active' ? (
                  <div role="status" aria-live="polite" className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="font-medium text-grayscale-1300">
                      Deep search results · {results.length.toLocaleString()} {results.length === 1 ? 'technology' : 'technologies'}
                    </span>
                    {quickSnapshotRef.current && (
                      <button type="button" onClick={restoreQuickResults}
                        className="text-primary-700 hover:underline">
                        Back to quick results
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <span className="flex-1 min-w-[14rem]">
                      Not seeing what you need? Try adding more specific search terms — or run a
                      deep search that reads the results with AI (takes ~10-15 s).
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      <button type="button" onClick={runDeepSearch} disabled={loading}
                        className="px-3 py-1 rounded-full bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50 font-medium">
                        Run deep search
                      </button>
                      <button type="button" onClick={dismissDeepOffer}
                        className="px-2 py-1 rounded-full text-grayscale-1100 hover:text-grayscale-1300 hover:bg-grayscale-100">
                        No thanks
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
            {/* Filters the AI pulled out of the query, e.g. "trl 6" → TRL 6.
                Rendered for empty result sets too — "0 matches" makes far more
                sense when you can see the filters that were applied. */}
            {!showFullLoader && hasSearched && aiApplied.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mb-3">
                <span className="text-xs text-grayscale-1100">✨ Applied from your query:</span>
                {aiApplied.map(chip => (
                  <span
                    key={chip}
                    className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary-100 text-primary-og-600 text-xs font-medium"
                  >{chip}</span>
                ))}
              </div>
            )}
            {/* Filters found in the query but withheld: this account cannot
                filter, so the results are NOT narrowed to them. Said plainly,
                with the same upgrade sheet the locked filter panel opens. */}
            {!showFullLoader && hasSearched && !upgrade.ent.can_filter && lockedDetected.length > 0 && (
              <div
                data-testid="detected-filter-locked"
                className="mb-3 rounded-xl border border-primary-200 bg-primary-50/60 px-3 py-2 text-xs text-grayscale-1200 flex flex-wrap items-center gap-x-3 gap-y-2"
              >
                <span className="flex items-start gap-1.5">
                  <svg className="w-3.5 h-3.5 mt-px shrink-0 text-primary-700" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.8" />
                  </svg>
                  <span>
                    Auto-detected filter:{' '}
                    <span className="font-medium text-grayscale-1300">{lockedDetected.join(' · ')}</span>
                    {' '}— upgrade to apply it and see those results.
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    trackClick('detected_filter_locked', { labels: lockedDetected, query: appliedSearch?.query || query });
                    upgrade.gate(upgrade.ent.can_filter, 'full filtering', undefined, 'can_filter',
                      { control: 'filter', filter: 'detected', surface: lockSurface });
                  }}
                  className="px-3 py-1 rounded-full bg-primary-500 text-white hover:bg-primary-600 font-medium shrink-0"
                >
                  Upgrade to apply
                </button>
              </div>
            )}
            {landingView && !showFullLoader && (
              // No picks answer yet (picksMeta is set by every load, even a
              // failed one): placeholders — not "couldn't load" for the frame
              // between the landing being decided and its request starting.
              landingMode === 'wait' || (picksCards.length === 0 && (picksLoading || !picksMeta)) ? (
                <PicksSkeleton withHeading={landingMode === 'picks'} />
              ) : (
                <ExplorerPicks
                  cards={picksCards}
                  loading={picksLoading}
                  mode={picksMode}
                  industry={picksMeta?.industry ?? null}
                  keyword={picksMeta?.keyword ?? null}
                  copy={landing?.copy}
                  design={cardDesign}
                  openCardId={openCardId}
                  onToggleCard={setOpenCardId}
                  savedIds={savedIds}
                  onBookmarkChange={(ids, on) => setSavedIds(prev => {
                    const next = new Set(prev);
                    ids.forEach(k => { if (on) next.add(k); else next.delete(k); });
                    return next;
                  })}
                  onContact={card => onContact?.(card, 'cosmos_picks')}
                  onReadMore={id => onReadMore && onReadMore(id)}
                  onUniversityClick={code => filterFromCard('university_codes', code)}
                  techCount={typeof facets?.total_technologies === 'number' && facets.total_technologies > 0
                    ? facets.total_technologies : null}
                  trial={trial}
                  onStartTrial={onStartTrial}
                />
              )
            )}
            {!landingView && !loading && results.length === 0 && (
              <div className="text-sm text-grayscale-1100">
                {!hasSearched || total !== 0 ? (
                  // The server's line wins when it sent one: it knows whether
                  // the filters were simply absent or stripped by the paywall,
                  // and those need different next steps from the reader.
                  emptyNotice || 'Type a query or pick a filter, then hit Search.'
                ) : (
                  // "No results found." on its own left people stuck, because
                  // the usual cause is a filter combination with nothing in it
                  // (e.g. Australia + TRL 8, which is genuinely empty) and the
                  // filters are in a panel they may have collapsed.
                  <div className="flex flex-col items-start gap-2">
                    <div className="text-grayscale-1300 font-medium">
                      No technologies match {query.trim() ? <>“{query.trim()}”</> : 'these filters'}
                      {!filtersAreEmpty(filters) && query.trim() ? ' with these filters' : ''}.
                    </div>
                    {!filtersAreEmpty(filters) ? (
                      <>
                        <span>
                          The filters are the most likely cause — try removing one, or
                          clear them to search the whole corpus.
                        </span>
                        <button type="button" onClick={clearFilters}
                          className="px-3 py-1.5 rounded-full border border-primary-300 text-primary-700 text-xs hover:bg-primary-100/40">
                          Clear all filters
                        </button>
                      </>
                    ) : (
                      <span>Try a broader phrase, or fewer words.</span>
                    )}
                  </div>
                )}
              </div>
            )}
            {!landingView && !showFullLoader && results.length > 0 && (
              // Inline loads (paging from the cache, a deep search) keep the
              // list on screen, dimmed and inert, instead of the full loader.
              <div
                aria-busy={inlineBusy || undefined}
                className={inlineBusy ? 'opacity-50 pointer-events-none transition-opacity' : 'transition-opacity'}
              >
                {inlineBusy && deepState !== 'running' && (
                  <div role="status" className="flex items-center gap-2 text-xs text-grayscale-1100 mb-2">
                    <Spinner className="h-3.5 w-3.5" />
                    Loading page {pendingPage + 1}…
                  </div>
                )}
                <div className="text-xs text-grayscale-1100 mb-3">
                  {hasSearched
                    ? <>{hasMoreBeyondCap && totalUncapped > total
                          // "100 matches of 1,200 results" — the agreed wording.
                          // The browsable set is capped at 100; hiding the real
                          // total made a 1,200-hit search look like 100 hits.
                          ? <>{total.toLocaleString()} matches of {totalUncapped.toLocaleString()} results</>
                          : <>{total.toLocaleString()} match{total === 1 ? '' : 'es'}</>}
                        {' · '}page {page + 1} of {totalPages}
                        {correctedQuery && (
                          <>{' · '}Showing results for <span className="font-semibold text-grayscale-1300">{correctedQuery}</span></>
                        )}
                        {/* Routing internals for signed-in admins only; for
                            everyone else they stay in the dev console. */}
                        {isAdmin && responseMeta
                          && responseMeta.settings?.show_route_badge !== false && (
                          <span
                            className="ml-2 inline-flex items-center rounded px-1.5 py-0.5 bg-grayscale-100 text-[10px] font-mono text-grayscale-1000 align-middle"
                            title="Search routing (admins only): route · engine · classification source · cache · total time"
                          >
                            {[
                              responseMeta.deep_search_forced ? 'deep' : (responseMeta.route || '?'),
                              responseMeta.engine || null,
                              responseMeta.classification?.source || null,
                              responseMeta.served_from_cache ? 'cached' : null,
                              typeof responseMeta.timings?.total_ms === 'number'
                                ? `${Math.round(responseMeta.timings.total_ms)} ms` : null,
                            ].filter(Boolean).join(' · ')}
                          </span>
                        )}</>
                    : <>Popular technologies — type a query above to search</>}
                </div>
                {cardDesign === 'accordion' ? (
                <ul className="space-y-3">
                  {results.map((r: any, i: number) => {
                    const m = r.metadata || {};
                    const techId = String(m.utech_technology_id || m.id || '');
                    if (!techId) return null;
                    const cardKey = `${techId}#${i}`;
                    return (
                      <li key={`${r.algorithm || 'r'}-${i}-${techId}`}>
                        <TechAccordionCard
                          techId={techId}
                          preview={{
                            title: m.utech_title || m.title || 'Untitled',
                            university: m.university_name || '',
                            country: m.university_country || '',
                            trl: m.calculated_trl,
                            summary: m.short_summary || firstSentence(m.utech_summary || ''),
                          }}
                          // AI-scored searches: their relevance. Quick searches:
                          // the keyword match the server sorted them by
                          // (meta.score_kind "keyword"), else worked out here.
                          // Popular technologies (no query) and a query that was
                          // only a filter (score_kind "filter"): no pill.
                          relevance={resultRelevance(r, relevanceQuery, !!responseMeta?.scores_are_relevance, responseMeta?.score_kind).value}
                          relevanceKind={resultRelevance(r, relevanceQuery, !!responseMeta?.scores_are_relevance, responseMeta?.score_kind).kind}
                          expanded={openCardId === cardKey}
                          onToggle={open => setOpenCardId(open ? cardKey : null)}
                          rank={page * pageSize + i + 1}
                          surface={hasSearched ? 'search' : 'popular'}
                          bookmarked={savedIds.has(techId)}
                          onBookmarkChange={(id, on) => setSavedIds(prev => {
                            const next = new Set(prev);
                            [id, techId].forEach(k => { if (on) next.add(k); else next.delete(k); });
                            return next;
                          })}
                          onContact={onContact}
                          onOpenTech={id => onReadMore && onReadMore(id)}
                          onUniversityClick={u => filterFromCard('university_codes', u.code || m.utech_university_id)}
                        />
                      </li>
                    );
                  })}
                </ul>
                ) : (
                <ul className="space-y-3">
                  {results.map((r: any, i: number) => {
                    const m = r.metadata || {};
                    const title = m.utech_title || m.title || 'Untitled';
                    const university = m.university_name || '';
                    const country = m.university_country || '';
                    const trl = m.calculated_trl;
                    const summary = m.short_summary || firstSentence(m.utech_summary || '');
                    const techId = m.utech_technology_id || m.id;
                    return (
                      <li
                        key={`${r.algorithm || 'r'}-${i}-${techId}`}
                        className="border border-grayscale-200 rounded-xl p-4 hover:shadow-md transition-shadow cursor-pointer utech-nocopy"
                        onClick={() => onReadMore && techId && onReadMore(
                          String(techId),
                          // Only when the score is a relevance judgement. Without
                          // an LLM rescore the backend falls back to 1 - i/n, so
                          // the 24th of 25 results rendered as "4% relevance"
                          // however well it matched — a number that looked like a
                          // verdict and was really a row position.
                          responseMeta?.scores_are_relevance && typeof r.percentage === 'number'
                            ? r.percentage : undefined,
                        )}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="font-semibold text-grayscale-1300">{title}</h3>
                          {trl !== null && trl !== undefined && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">TRL {trl}</span>
                          )}
                        </div>
                        <div className="text-xs text-grayscale-1100 mt-1">
                          {[university, country].filter(Boolean).join(' · ')}
                        </div>
                        {summary && (
                          <p className="text-sm text-grayscale-1200 mt-2 line-clamp-2 overflow-hidden">{summary}</p>
                        )}
                      </li>
                    );
                  })}
                </ul>
                )}

                {/* The "free preview limit" banner was removed (owner, 2026-09-29):
                    it told people to e-mail us instead of offering the upgrade,
                    and explorers can search in full now; the upgrade prompts
                    (components/upgrade) carry that message. */}
              </div>
            )}
          </div>

          {/* Pagination — sticky bar so it's always visible regardless of result count */}
          {totalPages > 1 && hasSearched && appliedSearch && (
            <div className="border-t border-grayscale-200 px-4 py-2 flex items-center justify-center gap-3 bg-white">
              <button onClick={() => goToPage(page - 1)} disabled={page === 0 || loading}
                className="px-3 py-1.5 text-sm border border-grayscale-300 rounded disabled:opacity-50 hover:bg-grayscale-50">
                ← Prev
              </button>
              <span className="text-sm text-grayscale-1200">
                Page <span className="font-semibold">{page + 1}</span> / {totalPages}
                {hasMoreBeyondCap && <span className="text-grayscale-1000"> (capped)</span>}
              </span>
              <button onClick={() => goToPage(page + 1)} disabled={page >= totalPages - 1 || loading}
                className="px-3 py-1.5 text-sm border border-grayscale-300 rounded disabled:opacity-50 hover:bg-grayscale-50">
                Next →
              </button>
            </div>
          )}
        </section>

        {/* Right history pane — collapsible (mirrors the filter sidebar
            UX). Visible on wide screens (lg+); below that it's accessed via
            a button + slide-in drawer (see below). Invite/Feedback/Bug live
            at the top of this column. */}
        {historyCollapsed ? (
          <aside className="hidden lg:flex shrink-0 w-10 border-l border-grayscale-200 bg-white items-start justify-center pt-3">
            <button
              onClick={() => setHistoryCollapsed(false)}
              className="w-8 h-10 flex items-center justify-center text-grayscale-1200 hover:text-grayscale-1300 hover:bg-grayscale-100 rounded transition-colors"
              title="Expand search history"
              aria-label="Expand search history"
            >
              {/* left-chevron — points where the panel will expand to */}
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </aside>
        ) : (
          <aside className="w-64 xl:w-72 shrink-0 border-l border-grayscale-200 overflow-y-auto px-3 py-4 hidden lg:block bg-white">
            <div className="mb-4 pb-3 border-b border-grayscale-200">
              {/* Not subscribed: "Upgrade to Voyager" here, in place of
                  Invite / Feedback / Bug (the owner's mock); subscribers keep
                  those. The history drawer on a phone still has them. */}
              {/* The Explorer landing has its button beside the search box. */}
              {searchButtonOn && !explorerView ? <UpgradeSearchButton variant="pane" /> : renderActionButtons()}
            </div>
            <div className="flex items-center justify-between gap-2 mb-3 flex-nowrap">
              <h2 className="text-sm font-semibold text-grayscale-1300 whitespace-nowrap">Search history</h2>
              <div className="flex items-center gap-2 shrink-0">
                {history.length > 0 && (
                  <button
                    onClick={clearAllHistory}
                    className="text-[11px] whitespace-nowrap text-grayscale-1100 hover:text-red-600 hover:underline"
                    title="Clear search history"
                  >Clear all</button>
                )}
                <button
                  onClick={() => setHistoryCollapsed(true)}
                  className="text-grayscale-1200 hover:text-grayscale-1300 w-8 h-8 flex items-center justify-center rounded hover:bg-grayscale-100 transition-colors"
                  title="Collapse search history"
                  aria-label="Collapse search history"
                >
                  {/* right-chevron */}
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
            {renderHistoryBody({ hideHeader: true, onPick: () => {} })}
          </aside>
        )}
      </div>

      {/* History drawer — used on viewports < lg (mobile + narrow desktop
          when browser side-panels eat the width). Slides in from the right.
          Solid white backdrop + z-[60] so the mobile "Cosmos" header /
          side nav don't bleed through. */}
      {showHistoryDrawer && (
        <div className="fixed inset-0 z-[60] lg:hidden flex bg-white">
          {/* Drawer occupies the full viewport — no visible edge or shadow
              seam between the panel and the surrounding area. */}
          <aside className="relative h-full w-full bg-white overflow-y-auto p-4 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-grayscale-1300">Search history</h2>
              <div className="flex items-center gap-3">
                {history.length > 0 && (
                  <button onClick={clearAllHistory} className="text-[11px] text-grayscale-1100 hover:text-red-600 hover:underline" title="Clear search history">Clear all</button>
                )}
                <button onClick={() => setShowHistoryDrawer(false)}
                  className="text-grayscale-1100 hover:text-grayscale-1300 text-xl leading-none w-7 h-7 flex items-center justify-center" aria-label="Close history">×</button>
              </div>
            </div>
            {/* Same action buttons rendered on desktop — surfaced inside the
                drawer so mobile users still have a path to Invite/Feedback/Bug. */}
            <div className="mb-4 pb-3 border-b border-grayscale-200">
              {renderActionButtons()}
            </div>
            <div className="flex-1">
              {renderHistoryBody({ hideHeader: true, onPick: () => setShowHistoryDrawer(false) })}
            </div>
          </aside>
        </div>
      )}
    </main>
  );
};

// ---------- helpers ----------
// Spinner, FilterSection and CheckboxList are OCEAN's filter pane too
// (components/ocean/Discovery.tsx), so the two pages look the same.

export const Spinner: React.FC<{ className?: string }> = ({ className = 'h-4 w-4' }) => (
  <svg className={`animate-spin text-primary-600 ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

function firstSentence(text: string): string {
  if (!text) return '';
  const t = text.trim();
  const m = t.match(/^[\s\S]*?[.!?](?=\s|$)/);
  let out = m ? m[0] : t;
  if (out.length > 220) out = out.slice(0, 220).trimEnd() + '…';
  return out;
}

export const FilterSection: React.FC<{
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ title, isOpen, onToggle, children }) => (
  <div className="border border-grayscale-200 rounded-lg bg-white">
    <button onClick={onToggle}
      className="w-full flex items-center justify-between p-2.5 text-sm font-medium text-grayscale-1300">
      <span>{title}</span>
      {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
    </button>
    {isOpen && <div className="p-2.5 pt-0">{children}</div>}
  </div>
);

export const CheckboxList: React.FC<{
  options: { value: string; label: string; count?: number | null; searchAliases?: string[] }[];
  selected: string[];
  onToggle: (v: string) => void;
  searchable?: boolean;
  /**
   * When true, renders options in the exact order given. Otherwise the list
   * is re-sorted (selected first, then by count descending) for usability —
   * which is great for unbounded lists like Universities but wrong for
   * naturally-ordered lists like TRL 1..9.
   */
  preserveOrder?: boolean;
  /**
   * When true, allows option labels to wrap to multiple lines instead of
   * truncating with an ellipsis. Used for long entries like university names
   * in the (narrow) filter sidebar.
   */
  wrap?: boolean;
  /**
   * When true, sorts options alphabetically by label (selected entries
   * still float to the top). Use this for unbounded human-readable lists
   * like Country, University, and Industry where users scan by name —
   * count-desc sorting reshuffles entries every time facet counts change,
   * which is jarring while skimming. Mutually exclusive with `preserveOrder`.
   *
   * Values with no matches still sink below the ones that have some. The
   * option list is the whole corpus — hundreds of universities — while the
   * counts describe the results actually retrieved, so a purely alphabetical
   * list opened on a screenful of zeros and read as "the filters are broken".
   * Alphabetical within each group keeps the order stable while skimming,
   * which is what this flag is for.
   */
  sortAlpha?: boolean;
}> = ({ options, selected, onToggle, searchable = false, preserveOrder = false, wrap = false, sortAlpha = false }) => {
  const [filterText, setFilterText] = useState('');
  const sortedOptions = useMemo(() => {
    if (preserveOrder) return options;
    if (sortAlpha) {
      return [...options].sort((a, b) => {
        const aSelected = selected.includes(a.value);
        const bSelected = selected.includes(b.value);
        if (aSelected !== bSelected) return aSelected ? -1 : 1;
        // Unknown counts (no search run yet) count as available, so the list
        // is plainly alphabetical until there are results to describe.
        const aHas = (a.count ?? 1) > 0;
        const bHas = (b.count ?? 1) > 0;
        if (aHas !== bHas) return aHas ? -1 : 1;
        return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
      });
    }
    const anyCounts = options.some(o => o.count !== null && o.count !== undefined);
    if (!anyCounts) return options;
    return [...options].sort((a, b) => {
      const ac = a.count ?? -1;
      const bc = b.count ?? -1;
      // selected entries always at top so the user doesn't lose them
      const aSelected = selected.includes(a.value);
      const bSelected = selected.includes(b.value);
      if (aSelected !== bSelected) return aSelected ? -1 : 1;
      return bc - ac;
    });
  }, [options, selected, preserveOrder, sortAlpha]);
  const filtered = useMemo(() => {
    if (!filterText.trim()) return sortedOptions;
    const q = filterText.toLowerCase();
    return sortedOptions.filter(o => {
      if (o.label.toLowerCase().includes(q)) return true;
      // Match short-form aliases too (e.g. "IIT Bombay" matches
      // "Indian Institute of Technology Bombay").
      if (o.searchAliases?.some(a => a.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [filterText, sortedOptions]);

  return (
    <div>
      {searchable && (
        <input
          type="text"
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          placeholder="Filter…"
          className="w-full mb-2 px-2 py-1 text-xs border border-grayscale-300 rounded"
        />
      )}
      <div className="max-h-48 overflow-y-auto space-y-1">
        {filtered.length === 0 && <div className="text-xs text-grayscale-1100">No matches</div>}
        {filtered.map(opt => {
          const checked = selected.includes(opt.value);
          // count === null means counts haven't been computed yet (no search run).
          // count === 0 means this value has no records in current result set.
          //
          // Per product (2026-09): filter values are NEVER disabled, even when
          // their live facet count is 0. Previously a 0-count cross-facet value
          // was greyed out + unclickable, which blocked users from stacking an
          // additional filter onto an existing selection. Now every value stays
          // clickable so filters can always be added; the count is still shown
          // (incl. "0") as a hint that the combination may narrow results.
          const hasCount = opt.count !== null && opt.count !== undefined;
          const isZero = hasCount && (opt.count as number) === 0 && !checked;
          return (
            <label key={opt.value}
              className="flex items-start gap-2 text-xs text-grayscale-1300 cursor-pointer">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(opt.value)}
                className="w-3.5 h-3.5 mt-0.5 shrink-0"
              />
              {/* 0-count values are still selectable; we only dim the label
                  slightly so the user can tell the combo currently has no hits. */}
              <span className={`flex-1 ${wrap ? 'break-words' : 'truncate'} ${isZero ? 'opacity-60' : ''}`}>{opt.label}</span>
              {hasCount && (
                <span className={`text-[10px] tabular-nums shrink-0 ${isZero ? 'text-grayscale-700' : 'text-grayscale-1000'}`}>
                  {(opt.count as number).toLocaleString()}
                </span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Error boundary that wraps the page so an unforeseen render error in any
 * descendant — e.g. a backend response with a shape we didn't anticipate —
 * shows a friendly fallback instead of crashing the rest of the SPA.
 *
 * The error gets logged to the console (and any analytics hook) so you can
 * still diagnose deploys, but the user sees a soft "Something went wrong"
 * card with a Reload button rather than a blank screen.
 */
class DirectSearchErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('DirectSearchPage crashed:', error, info?.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <main className="flex flex-1 flex-col self-stretch rounded-2xl bg-white shadow-custom-light p-6 items-center justify-center">
          <div className="max-w-md text-center">
            <h2 className="text-lg font-semibold text-grayscale-1300 mb-2">Direct Search hit an error</h2>
            <p className="text-sm text-grayscale-1100 mb-4">
              Something unexpected happened while rendering this page. Reload to try again, or
              switch to Cosmos in the meantime.
            </p>
            <pre className="text-[11px] text-grayscale-1000 bg-grayscale-50 border border-grayscale-200 rounded p-2 mb-4 text-left overflow-x-auto">
              {String(this.state.error?.message || this.state.error)}
            </pre>
            <button
              onClick={() => { this.setState({ error: null }); }}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600"
            >Try again</button>
          </div>
        </main>
      );
    }
    return this.props.children as React.ReactElement;
  }
}

export const DirectSearchPage: React.FC<DirectSearchPageProps> = (props) => (
  <DirectSearchErrorBoundary>
    <DirectSearchPageInner {...props} />
  </DirectSearchErrorBoundary>
);

export type { DirectSearchPageProps };

export default DirectSearchPage;
